import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  Slider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  TextField,
  LinearProgress,
  Stack,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Collections as CollectionsIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Store as StoreIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import axios from 'axios';
import PromptGeneratorPro from '../../components/PromptGenerator/PromptGeneratorPro';
import { fetchBrandPlanning } from '../../api/brands';
import {
  fetchDropConfiguration,
  saveDropConfiguration,
  fetchWholesaleCatalog,
  fetchPatternCatalog,
  fetchFabricCatalog,
  deleteDropAsset,
} from '../../api/designStudio';
import CollectionPlanWorkspace from '../../components/CollectionPlan/CollectionPlanWorkspace';
import KamHeroHeader from '../../components/KamHeroHeader/KamHeroHeader';
import WholesaleCatalogBrowser from '../../components/Design/WholesaleCatalogBrowser';
import CutAndSewCostForm from '../../components/Design/CutAndSewCostForm';
import WhiteLabelCostForm from '../../components/Design/WhiteLabelCostForm';

const formatCurrency = (value) => {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: safeValue >= 1000 ? 0 : 2,
  }).format(safeValue);
};

const getRandom = (list) => (Array.isArray(list) && list.length > 0 ? list[Math.floor(Math.random() * list.length)] : '');

const formatSegment = (value, length = 3, fallback = '') => {
  const candidate = value ? value.toString() : '';
  const sanitized = candidate.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (sanitized.length === 0) {
    const fb = fallback ? fallback.toString().toUpperCase().replace(/[^A-Z0-9]/g, '') : '';
    if (fb.length === 0) {
      return ''.padEnd(length, 'X').slice(0, length);
    }
    return fb.padEnd(length, 'X').slice(0, length);
  }
  return sanitized.padEnd(length, 'X').slice(0, length);
};

const formatPercent = (value) => {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  return `${Math.round(safeValue * 100)}%`;
};

const formatUnits = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toLocaleString() : '0';
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.valueOf())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
};

const ASSET_CATEGORIES = ['Design Asset', 'Production Reference', 'Tech Pack Asset'];
const DEFAULT_NEGATIVE_PROMPT = 'low quality, blurry, distorted, ugly';

const createDefaultCutAndSewSchema = () => ({
  metadata: {
    garment_name: '',
    garment_type: '',
    brand: '',
    collection: '',
    season: '',
    designer: '',
    category: '',
    gender: '',
    style_code: ''
  },
  sizing_system: {
    base_size: '',
    size_standard: '',
    fit_intention: '',
    ease_allowance: {
      chest_ease: '',
      waist_ease: '',
      hip_ease: ''
    }
  },
  construction: {
    materials: {
      main_fabric: {
        name: '',
        composition: '',
        weight: '',
        color: '',
        supplier: '',
        notes: ''
      },
      trims: []
    },
    pattern_specifications: {
      total_pattern_pieces: '',
      pattern_pieces: []
    },
    components: {
      neckline: {
        style: '',
        finish: ''
      },
      sleeves: {
        style: '',
        length: ''
      },
      hemline: {
        style: '',
        finish: '',
        hem_depth: ''
      },
      pockets: []
    },
    construction_sequence: []
  },
  labeling_packaging: {
    brand_label: {
      placement: '',
      dimensions: '',
      material: ''
    },
    care_label: {
      placement: '',
      instructions: ''
    },
    packaging: {
      fold_method: '',
      bag_type: '',
      carton_quantity: ''
    }
  },
  artwork: {
    placements: []
  },
  ai_generation: {
    style_keywords: [],
    background_style: '',
    priority_features: []
  },
  notes: {
    factory_instructions: '',
    designer_notes: ''
  }
});

const createDefaultWhiteLabelSchema = () => ({
  metadata: {
    garment_name: '',
    base_blank_sku: '',
    supplier: '',
    customization_type: [],
    collection: '',
    brand: '',
    season: ''
  },
  visual_assets: {
    mockup_front: '',
    mockup_back: '',
    color_swatches: []
  },
  blank_specifications: {
    body: {
      fabric: '',
      weight: '',
      fit: '',
      size_range: ''
    },
    availability: {
      lead_time_weeks: '',
      warehouse_location: '',
      minimum_order_qty: ''
    }
  },
  embellishments: [],
  labeling_packaging: {
    relabel: false,
    brand_label_type: '',
    care_label: '',
    packaging: ''
  },
  ai_generation: {
    style_keywords: [],
    background_style: '',
    prompt_notes: ''
  },
  notes: {
    fulfillment_notes: '',
    creative_notes: ''
  }
});

const ensureProductInfoSchemas = (info = {}) => {
  const schemas = info.schemas || {};
  return {
    cutAndSew: schemas.cutAndSew || createDefaultCutAndSewSchema(),
    whiteLabel: schemas.whiteLabel || createDefaultWhiteLabelSchema()
  };
};

const getSchemaKeyForMode = (mode) => (mode === 'cut_and_sew' ? 'cutAndSew' : 'whiteLabel');

const getDefaultSchemaForMode = (mode) =>
  mode === 'cut_and_sew' ? createDefaultCutAndSewSchema() : createDefaultWhiteLabelSchema();

const applySummaryFieldToSchema = (schemaKey, schema, field, value) => {
  const base =
    schema ||
    (schemaKey === 'cutAndSew' ? createDefaultCutAndSewSchema() : createDefaultWhiteLabelSchema());
  const next = { ...base };
  const ensureObject = (obj) => (obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {});

  if (schemaKey === 'cutAndSew') {
    if (['name', 'season', 'collection', 'sku', 'fit'].includes(field)) {
      next.metadata = ensureObject(next.metadata);
      if (field === 'name') next.metadata.garment_name = value;
      if (field === 'season') next.metadata.season = value;
      if (field === 'collection') next.metadata.collection = value;
      if (field === 'sku') next.metadata.style_code = value;
      if (field === 'fit') next.metadata.garment_type = value;
    }
    if (field === 'fit') {
      next.sizing_system = ensureObject(next.sizing_system);
      next.sizing_system.fit_intention = value;
    }
    if (field === 'sizeRun') {
      next.sizing_system = ensureObject(next.sizing_system);
      next.sizing_system.size_standard = value;
    }
    if (field === 'material') {
      next.construction = ensureObject(next.construction);
      next.construction.materials = ensureObject(next.construction.materials);
      next.construction.materials.main_fabric = ensureObject(next.construction.materials.main_fabric);
      next.construction.materials.main_fabric.composition = value;
    }
    if (field === 'technicalNotes') {
      next.notes = ensureObject(next.notes);
      next.notes.factory_instructions = value;
    }
    if (field === 'productionNotes') {
      next.notes = ensureObject(next.notes);
      next.notes.designer_notes = value;
    }
    if (field === 'careInstructions') {
      next.labeling_packaging = ensureObject(next.labeling_packaging);
      next.labeling_packaging.care_label = ensureObject(next.labeling_packaging.care_label);
      next.labeling_packaging.care_label.instructions = value;
    }
  } else {
    if (['name', 'collection', 'season'].includes(field)) {
      next.metadata = ensureObject(next.metadata);
      if (field === 'name') next.metadata.garment_name = value;
      if (field === 'collection') next.metadata.collection = value;
      if (field === 'season') next.metadata.season = value;
    }
    if (field === 'sku') {
      next.metadata = ensureObject(next.metadata);
      next.metadata.base_blank_sku = value;
    }
    if (field === 'material') {
      next.blank_specifications = ensureObject(next.blank_specifications);
      next.blank_specifications.body = ensureObject(next.blank_specifications.body);
      next.blank_specifications.body.fabric = value;
    }
    if (field === 'fit') {
      next.blank_specifications = ensureObject(next.blank_specifications);
      next.blank_specifications.body = ensureObject(next.blank_specifications.body);
      next.blank_specifications.body.fit = value;
    }
    if (field === 'sizeRun') {
      next.blank_specifications = ensureObject(next.blank_specifications);
      next.blank_specifications.body = ensureObject(next.blank_specifications.body);
      next.blank_specifications.body.size_range = value;
    }
    if (field === 'technicalNotes') {
      next.notes = ensureObject(next.notes);
      next.notes.creative_notes = value;
    }
    if (field === 'productionNotes') {
      next.notes = ensureObject(next.notes);
      next.notes.fulfillment_notes = value;
    }
    if (field === 'careInstructions') {
      next.labeling_packaging = ensureObject(next.labeling_packaging);
      next.labeling_packaging.care_label = value;
    }
  }

  return next;
};

const GRAPHIC_PROMPT_PRESETS = [
  'Logo Lockup: clean vector chest print, centered, bold brand mark with tagline',
  'Back Graphic: oversized illustration, gradient inks, streetwear aesthetic, layered textures',
  'All-over Pattern: repeating geometric motif, monochrome, seamless tiling for cut-and-sew',
  'Sleeve Detail: typographic stripe reading studio name, vertical placement, reflective ink'
];

const DEFAULT_GRAPHIC_FORM = {
  style: '',
  placement: '',
  technique: '',
  palette: '',
  motif: '',
  finish: ''
};

const GRAPHIC_PLACEMENTS = [
  'Front chest',
  'Full front',
  'Back panel',
  'Sleeves',
  'Hem label',
  'Collar interior'
];

const GRAPHIC_TECHNIQUES = [
  'Screen print',
  'Puff ink',
  'High density print',
  'Discharge print',
  'Embroidery',
  'Chain stitch embroidery',
  'Chenille patch',
  'Heat transfer'
];

const GRAPHIC_STYLES = [
  'Retro sports badge',
  'Minimal typographic lockup',
  'Hand drawn illustration',
  'Graffiti street art',
  'Abstract geometric',
  'Photo collage',
  'Y2K chrome',
  'Psychedelic swirl'
];

const GRAPHIC_PALETTES = [
  'Monochrome black & bone',
  'Vintage collegiate (maroon, cream, gold)',
  'Neon duo (magenta, electric blue)',
  'Earth tones (olive, sand, rust)',
  'Primary pop (red, yellow, blue)',
  'Ice tones (sky, mint, lilac)'
];

const GRAPHIC_FINISHES = [
  'Matte soft hand feel',
  'Gloss clear overlay',
  'Metallic foil pop',
  'UV reactive ink',
  'Glow-in-the-dark accents',
  'Crackle vintage wash'
];

const BRAND_LABEL_EXECUTIONS = [
  'Woven label',
  'Heat-transfer print',
  'Printed satin',
  'Tear-away + relabel',
  'Supplier default',
  'Other'
];

const BRAND_LABEL_LOCATIONS = ['Inside back neck', 'Hem', 'Waistband', 'Side seam', 'Cuff', 'Other'];

const CUSTOM_CARE_LABEL_VALUE = 'Custom brand label';
const LEGACY_CUSTOM_CARE_VALUES = ['Custom SIVAD label', 'Custom SIVAD tag'];
const CARE_STRATEGIES = [CUSTOM_CARE_LABEL_VALUE, 'Supplier default', 'Printed on garment', 'Hangtag only', 'Other'];

const normalizeCareStrategyValue = (value) => {
  if (!value) return value;
  if (LEGACY_CUSTOM_CARE_VALUES.includes(value)) {
    return CUSTOM_CARE_LABEL_VALUE;
  }
  return value;
};

const getCareStrategyLabel = (option, brandName) => {
  if (option !== CUSTOM_CARE_LABEL_VALUE) {
    return option;
  }
  const safeBrandName = (brandName || '').trim() || 'brand';
  return `Custom ${safeBrandName} label`;
};

const PACKAGING_FOLD_METHODS = ['Flat fold', 'Retail fold', 'Hanger', 'Rolled', 'Other'];

const PACKAGING_BAG_TYPES = ['Polybag with size sticker', 'Compostable bag', 'No bag', 'Retail box', 'Other'];

const PACKAGING_EXTRAS = ['Hangtag', 'Barcode sticker', 'Tissue wrap', 'Desiccant', 'Thank-you card', 'Dust bag'];

const CARE_LANGUAGES = ['English', 'Spanish', 'French', 'German', 'Italian', 'Japanese', 'Chinese', 'Korean'];

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const coerceIsoString = (value, fallback) => {
  if (value) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.valueOf())) {
      return parsed.toISOString();
    }
  }
  if (fallback) {
    const fallbackDate = new Date(fallback);
    if (!Number.isNaN(fallbackDate.valueOf())) {
      return fallbackDate.toISOString();
    }
  }
  return new Date().toISOString();
};

const normalizeAssetEntry = (asset, fallbackLookId = null) => {
  if (!asset || typeof asset !== 'object') {
    return null;
  }

  const idSource =
    asset.id ||
    (asset._id && typeof asset._id === 'object' && asset._id !== null
      ? asset._id.toString?.()
      : asset._id);
  const id =
    typeof idSource === 'string'
      ? idSource
      : idSource
      ? String(idSource)
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  const url = typeof asset.url === 'string' ? asset.url : '';
  if (!url) {
    return null;
  }

  const lookId =
    typeof asset.lookId === 'string' && asset.lookId.trim()
      ? asset.lookId.trim()
      : typeof asset.meta?.lookId === 'string' && asset.meta.lookId.trim()
      ? asset.meta.lookId.trim()
      : fallbackLookId || null;

  const name = typeof asset.name === 'string' ? asset.name : '';
  const categoryRaw = typeof asset.category === 'string' ? asset.category : '';
  const category = ASSET_CATEGORIES.includes(categoryRaw) ? categoryRaw : ASSET_CATEGORIES[0];
  const sourceType = typeof asset.sourceType === 'string' ? asset.sourceType : 'upload';

  const createdAt = coerceIsoString(asset.createdAt);
  const updatedAt = coerceIsoString(asset.updatedAt, createdAt);

  const metaSource = isPlainObject(asset.meta) ? asset.meta : {};
  const meta = Object.entries(metaSource).reduce((acc, [key, value]) => {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value === null
    ) {
      acc[key] = value;
    }
    return acc;
  }, {});

  if (lookId && !meta.lookId) {
    meta.lookId = lookId;
  }

  return {
    id,
    name,
    category,
    sourceType,
    url,
    lookId,
    meta,
    createdAt,
    updatedAt,
  };
};

const monthName = (month) => {
  const index = ((Number(month) || 1) - 1 + 12) % 12;
  return MONTH_LABELS[index];
};

const computeCycleDates = (cycle, referenceDate = new Date()) => {
  if (!cycle) {
    return {};
  }

  const startMonth = Number.isFinite(Number(cycle.startMonth))
    ? Math.min(Math.max(Number(cycle.startMonth), 1), 12)
    : 1;
  const launchOffset = Number.isFinite(Number(cycle.launchOffsetDays))
    ? Number(cycle.launchOffsetDays)
    : null;
  const shipOffset = Number.isFinite(Number(cycle.shipOffsetDays))
    ? Number(cycle.shipOffsetDays)
    : null;

  let startYear = referenceDate.getFullYear();
  let startDate = new Date(Date.UTC(startYear, startMonth - 1, 1));
  if (startDate < referenceDate) {
    startYear += 1;
    startDate = new Date(Date.UTC(startYear, startMonth - 1, 1));
  }

  const computeDate = (offset) => {
    if (!Number.isFinite(offset)) return null;
    const date = new Date(startDate.getTime() + offset * 86400000);
    return Number.isNaN(date.valueOf()) ? null : date.toISOString().slice(0, 10);
  };

  return {
    launchDate: computeDate(launchOffset),
    shipDate: computeDate(shipOffset),
  };
};

const buildCycleAutoUpdates = (cycle, productInfo = {}) => {
  if (!cycle) {
    return {};
  }
  const dates = computeCycleDates(cycle);
  const updates = {};
  if (!productInfo.season && cycle.season) {
    updates.season = cycle.season;
  }
  if (!productInfo.collection && cycle.theme) {
    updates.collection = cycle.theme;
  }
  if (!productInfo.launchDate && dates.launchDate) {
    updates.launchDate = dates.launchDate;
  }
  if (!productInfo.shipDate && dates.shipDate) {
    updates.shipDate = dates.shipDate;
  }
  return updates;
};

const describeDropCycle = (cycle) => {
  if (!cycle) {
    return '';
  }
  if (Array.isArray(cycle.months) && cycle.months.length > 0) {
    return cycle.months
      .map((month) => monthName(month))
      .join(' • ');
  }
  const start = monthName(cycle.startMonth);
  const end = monthName(cycle.endMonth);
  return start === end ? start : `${start} – ${end}`;
};

const DesignStudioPage = ({ mode = 'studio' }) => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dropOnlyMode = mode === 'dropOnly';

  // State
  const [numImages, setNumImages] = useState(1);
  const [qualitySteps, setQualitySteps] = useState(25);
  const [guidanceScale, setGuidanceScale] = useState(7.5);
  const [loading, setLoading] = useState(false);
  const [aiServerStatus, setAiServerStatus] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [lookGeneratedImages, setLookGeneratedImages] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [designName, setDesignName] = useState('');
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState(DEFAULT_NEGATIVE_PROMPT);
  const [graphicForm, setGraphicForm] = useState(DEFAULT_GRAPHIC_FORM);
  const [planning, setPlanning] = useState(null);
  const [planningLoading, setPlanningLoading] = useState(true);
  const [planningError, setPlanningError] = useState(null);
  const [activeNav, setActiveNav] = useState(dropOnlyMode ? 'budgetTracker' : 'collectionPlan');
  const [activeLookIndex, setActiveLookIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedBimesterId, setSelectedBimesterId] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [catalogBrowserOpen, setCatalogBrowserOpen] = useState(false);
  const [wholesaleCatalog, setWholesaleCatalog] = useState([]);
  const [patternCatalog, setPatternCatalog] = useState([]);
  const [fabricCatalog, setFabricCatalog] = useState([]);
  const [dropConfig, setDropConfig] = useState(null);
  const [dropBudget, setDropBudget] = useState(null);
  const [dropLoading, setDropLoading] = useState(false);
  const [dropSaving, setDropSaving] = useState(false);
  const [dropError, setDropError] = useState(null);
  const [dropDirty, setDropDirty] = useState(false);
  const [dropPlanningStatus, setDropPlanningStatus] = useState(null);
  const [unitGuardrailWarning, setUnitGuardrailWarning] = useState(null);
  const dropStateCacheRef = useRef({});
  const heroHeaderRef = useRef(null);
  const [heroHeaderHeight, setHeroHeaderHeight] = useState(0);
  const [assetLibrary, setAssetLibrary] = useState(() => {
    if (typeof window === 'undefined') {
      return [];
    }
    try {
      const stored = window.localStorage.getItem('designStudioAssetLibrary');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed
            .map((asset) => {
              const resolvedLookId = asset?.meta?.lookId ?? asset?.lookId ?? null;
              return normalizeAssetEntry(asset, resolvedLookId);
            })
            .filter(Boolean);
        }
      }
    } catch (error) {
      console.warn('Failed to parse asset library from storage', error);
    }
    return [];
  });
  const [assetUploadBusy, setAssetUploadBusy] = useState(false);
  const [assetCategorySelection, setAssetCategorySelection] = useState(ASSET_CATEGORIES[0]);
  const [lookPromptState, setLookPromptState] = useState({});
  const [checklistState, setChecklistState] = useState([]);

const brandSkuPrefix = useMemo(() => {
  const candidate =
    planning?.brand?.skuPrefix ||
    planning?.brand?.code ||
    planning?.brand?.abbreviation ||
      planning?.brand?.slug ||
      planning?.brand?.name ||
      planning?.meta?.brandCode ||
      planning?.meta?.brandSlug ||
    planning?.meta?.brandName ||
    '';
  return formatSegment(candidate, 3, 'BRD');
}, [planning]);

  const brandDisplayName = useMemo(() => {
    const candidate =
      planning?.brand?.name ||
      planning?.meta?.brandName ||
      dropConfig?.productInfo?.brand ||
      dropConfig?.productInfo?.metadata?.brand ||
      dropConfig?.brand?.name ||
      '';
    return (typeof candidate === 'string' && candidate.trim().length > 0) ? candidate : 'brand';
  }, [planning, dropConfig]);

  const updateLookPromptState = useCallback((lookId, partialState) => {
    if (!lookId) return;
    setLookPromptState((prev) => ({
      ...prev,
      [lookId]: {
        ...prev[lookId],
        ...partialState,
      },
    }));
  }, []);

  const handleNavClick = (section) => {
    setActiveNav(section);
  };

  // Check AI server health on mount
  useEffect(() => {
    checkAIServerHealth();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('designStudioAssetLibrary', JSON.stringify(assetLibrary));
    } catch (error) {
      console.warn('Failed to persist asset library', error);
    }
  }, [assetLibrary]);

  // Track hero header height for layout calculations
  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const updateHeight = () => {
      const nextHeight = heroHeaderRef.current?.getBoundingClientRect().height || 0;
      setHeroHeaderHeight(nextHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const chatPanelTop = heroHeaderHeight || 0;

  const checkAIServerHealth = async () => {
    try {
      const response = await fetch('/api/design-studio/health');
      const data = await response.json();
      setAiServerStatus(data.success ? 'online' : 'offline');
    } catch (error) {
      console.error('AI server health check failed:', error);
      setAiServerStatus('offline');
    }
  };

  useEffect(() => {
    const loadPlanning = async () => {
      if (!brandId) {
        setPlanningLoading(false);
        return;
      }

      setPlanningLoading(true);
      setPlanningError(null);
      try {
        const planningData = await fetchBrandPlanning(brandId, { includeHistory: false });
        const normalizedPlanning = planningData
          ? {
              ...planningData,
              lookConfigurations: Array.isArray(planningData.lookConfigurations)
                ? planningData.lookConfigurations
                : [],
            }
          : planningData;
        setPlanning(normalizedPlanning);
        setChecklistState(
          Array.isArray(normalizedPlanning?.timeline)
            ? normalizedPlanning.timeline.map((item) => ({
                label: item.label || 'Milestone',
                dueDate: item.dueDate ? new Date(item.dueDate) : null,
                status: item.status || 'pending',
                owner: item.owner || '',
                id: item.milestoneId || `${item.label}-${item.dueDate || ''}`,
              }))
            : []
        );
      } catch (error) {
        console.error('Failed to load planning guardrails', error);
        setPlanningError('Planning guardrails unavailable. Start a plan to unlock guardrails.');
      } finally {
        setPlanningLoading(false);
      }
    };

    loadPlanning();
  }, [brandId]);

  // Refresh planning data when returning from Garment Designer with saved design
  useEffect(() => {
    const refreshAfterDesignerSave = async () => {
      if (location.state?.fromDesigner && location.state?.designSaved && brandId) {
        console.log('🔄 Refreshing planning data after design save...');
        try {
          const planningData = await fetchBrandPlanning(brandId, { includeHistory: false });
          const normalizedPlanning = planningData
            ? {
                ...planningData,
                lookConfigurations: Array.isArray(planningData.lookConfigurations)
                  ? planningData.lookConfigurations
                  : [],
              }
            : planningData;
          setPlanning(normalizedPlanning);
          console.log('✅ Planning data refreshed with updated productInfo');
        } catch (error) {
          console.error('Failed to refresh planning data:', error);
        }

        // Clear the navigation state to prevent re-fetching on every render
        window.history.replaceState({}, document.title);
      }
    };

    refreshAfterDesignerSave();
  }, [location.state, brandId]);

  useEffect(() => {
    let isActive = true;

    const loadCatalogs = async () => {
      setCatalogLoading(true);
      setCatalogError(null);

      try {
        const [wholesaleRes, patternRes, fabricRes] = await Promise.all([
          fetchWholesaleCatalog().catch((error) => {
            console.error('Failed to load wholesale catalog', error);
            return { suppliers: [] };
          }),
          fetchPatternCatalog().catch((error) => {
            console.error('Failed to load pattern catalog', error);
            return { patterns: [], fabrics: [] };
          }),
          fetchFabricCatalog().catch((error) => {
            console.error('Failed to load fabric catalog', error);
            return { fabrics: [] };
          }),
        ]);

        if (!isActive) return;

        setWholesaleCatalog(Array.isArray(wholesaleRes?.suppliers) ? wholesaleRes.suppliers : []);
        setPatternCatalog(Array.isArray(patternRes?.patterns) ? patternRes.patterns : []);

        const fabricsFromPatterns = Array.isArray(patternRes?.fabrics) ? patternRes.fabrics : [];
        const fabricList = Array.isArray(fabricRes?.fabrics) ? fabricRes.fabrics : [];
        const fabricMap = new Map();

        fabricsFromPatterns.forEach((fabric) => {
          if (fabric && fabric.id) {
            fabricMap.set(fabric.id, fabric);
          }
        });

        fabricList.forEach((fabric) => {
          if (fabric && fabric.id && !fabricMap.has(fabric.id)) {
            fabricMap.set(fabric.id, fabric);
          }
        });

        setFabricCatalog(Array.from(fabricMap.values()));
      } catch (error) {
        if (!isActive) return;
        console.error('Catalog loading failed', error);
        setCatalogError(error.message || 'Unable to load design catalogs');
      } finally {
        if (isActive) {
          setCatalogLoading(false);
        }
      }
    };

    loadCatalogs();

    return () => {
      isActive = false;
    };
  }, []);

  // Drop cycle configuration
  const dropCycleOptions = useMemo(
    () => (Array.isArray(planning?.dropCycles) ? planning.dropCycles : []),
    [planning?.dropCycles]
  );

  // Provide a fallback bimester option so the dropdown isn't empty when planning lacks dropCycles
  const bimesterOptions = useMemo(() => {
    if (dropCycleOptions.length > 0) {
      return dropCycleOptions.map((cycle) => ({
        value: cycle.cycleId || cycle.label,
        label: cycle.label || cycle.cycleId,
      }));
    }
    // Fallback option keyed off any existing selection/drop config
    const fallbackValue = selectedBimesterId || dropConfig?.dropCycle || 'default-cycle';
    return [
      {
        value: fallbackValue,
        label: 'Default Cycle',
      },
    ];
  }, [dropCycleOptions, selectedBimesterId, dropConfig?.dropCycle]);

  const activeDropCycle = useMemo(() => {
    console.log('🔄 activeDropCycle recalculating:', {
      selectedBimesterId,
      'dropConfig.dropCycle': dropConfig?.dropCycle,
      'dropConfig.lookId': dropConfig?.lookId
    });

    // First use the globally selected bimester (user's manual selection takes priority)
    if (selectedBimesterId) {
      const cycle = dropCycleOptions.find((cycle) => cycle.cycleId === selectedBimesterId) || null;
      console.log('✅ Using selectedBimesterId:', selectedBimesterId, cycle?.label);
      return cycle;
    }
    // Otherwise use the current look's assigned drop cycle
    if (dropConfig?.dropCycle) {
      const cycle = dropCycleOptions.find((cycle) => cycle.cycleId === dropConfig.dropCycle) || null;
      console.log('⚠️ Falling back to dropConfig.dropCycle:', dropConfig.dropCycle, cycle?.label);
      return cycle;
    }
    // Default to the first drop cycle if none selected
    const defaultCycle = dropCycleOptions[0] || null;
    if (!defaultCycle && dropConfig?.dropCycle) {
      // Create a lightweight placeholder cycle so downstream calculations still render
      return {
        cycleId: dropConfig.dropCycle,
        label: 'Default Cycle',
        productMix: planning?.productMix || [],
        revenueShare: planning?.financialPlan?.revenueGoal ? 1 : 0,
      };
    }
    console.log('⚠️ Using default (first) cycle:', defaultCycle?.label);
    return defaultCycle;
  }, [dropCycleOptions, dropConfig?.dropCycle, selectedBimesterId]);

  // Annual budget calculations
  const annualRevenueGoal = planning?.financialPlan?.revenueGoal || 0;
  const marginTarget = planning?.financialPlan?.marginTarget || 0;
  const annualCogsBudget = annualRevenueGoal * Math.max(0, 1 - marginTarget);
  const annualGrossMargin = annualRevenueGoal * Math.max(0, marginTarget);

  // Bimester budget calculations (based on revenueShare allocation)
  const bimesterRevenueShare = activeDropCycle?.revenueShare || 0;
  const revenueGoal = annualRevenueGoal * bimesterRevenueShare;
  const totalCogsBudget = annualCogsBudget * bimesterRevenueShare;
  const totalGrossMargin = annualGrossMargin * bimesterRevenueShare;

  // Calculate bimester-specific planned looks and units from the active drop cycle's productMix
  const bimesterProductMix = activeDropCycle?.productMix || [];
  const bimesterPlannedLooks = bimesterProductMix.reduce(
    (sum, item) => sum + (Number.isFinite(item.plannedLooks) ? Math.max(item.plannedLooks, 0) : 0),
    0
  );
  const bimesterPlannedUnits = bimesterProductMix.reduce(
    (sum, item) => sum + (Number.isFinite(item.plannedUnits) ? Math.max(item.plannedUnits, 0) : 0),
    0
  );

  // Get annual totals for fallback calculations
  const annualPlannedLooks = planning?.summary?.plannedLooks || planning?.summary?.productCount || (planning?.productMix ? planning.productMix.length : 0) || 0;
  const annualPlannedUnits = planning?.summary?.projectedUnits || (planning?.productMix || []).reduce(
    (sum, item) => sum + (Number.isFinite(item.plannedUnits) ? Math.max(item.plannedUnits, 0) : 0),
    0
  );

  // Use bimester-specific values if configured, otherwise calculate proportional share based on revenue allocation
  const plannedLooksTarget = bimesterPlannedLooks > 0
    ? bimesterPlannedLooks
    : Math.round(annualPlannedLooks * bimesterRevenueShare);

  const effectiveUnits = bimesterPlannedUnits > 0
    ? bimesterPlannedUnits
    : Math.round(annualPlannedUnits * bimesterRevenueShare);

  const perLookCogsBudget = plannedLooksTarget > 0 ? totalCogsBudget / plannedLooksTarget : 0;
  const perLookGrossMargin = plannedLooksTarget > 0 ? totalGrossMargin / plannedLooksTarget : 0;
  const perUnitCogsBudget = effectiveUnits > 0 ? totalCogsBudget / effectiveUnits : 0;
  const perUnitGrossMargin = effectiveUnits > 0 ? totalGrossMargin / effectiveUnits : 0;
  const rawMaterialsShare =
    planning?.financialPlan?.costStructure?.materialsShare !== undefined
      ? planning.financialPlan.costStructure.materialsShare
      : 0.6;
  const materialsShare = Math.min(Math.max(rawMaterialsShare, 0), 1);
  const laborShare = 1 - materialsShare;

  const committedLooks = useMemo(() => {
    // Use only the active bimester's product mix to define looks

    // Debug logging
    console.log('🔍 CommittedLooks Debug:', {
      hasPlanning: !!planning,
      planningStatus: planning?.status,
      bimesterProductMixLength: bimesterProductMix?.length,
      bimesterProductMix: bimesterProductMix,
      selectedBimesterId,
      activeDropCycle: activeDropCycle?.label
    });

    if (
      !planning ||
      planning.status !== 'committed' ||
      !Array.isArray(bimesterProductMix) ||
      bimesterProductMix.length === 0
    ) {
      console.log('❌ No looks generated. Reason:', {
        noPlanning: !planning,
        notCommitted: planning?.status !== 'committed',
        noProductMix: !Array.isArray(bimesterProductMix) || bimesterProductMix.length === 0
      });
      return [];
    }

    const looks = [];

    bimesterProductMix.forEach((category, categoryIndex) => {
      const plannedLooks = Number.isFinite(category.plannedLooks) ? Math.max(category.plannedLooks, 0) : 0;
      console.log(`  Category ${category.categoryLabel}:`, { plannedLooks });

      if (plannedLooks === 0) {
        return;
      }

      const categoryLabel = category.categoryLabel || 'Look';
      const categoryKey = category.categoryId || categoryLabel || `category-${categoryIndex}`;

      for (let index = 0; index < plannedLooks; index += 1) {
        const lookId = `${categoryKey}-${index + 1}`;

        // Find corresponding lookConfiguration to get productInfo
        const lookConfig = planning?.lookConfigurations?.find(
          config => config.lookId === lookId
        );

        looks.push({
          id: lookId,
          lookId: lookId,
          tabLabel: `${categoryLabel} ${index + 1}`,
          categoryKey,
          categoryLabel,
          sequence: index + 1,
          category,
          // Include productInfo from lookConfiguration if available
          productInfo: lookConfig?.productInfo || {}
        });
      }
    });

    console.log('✅ Generated looks:', looks);
    return looks;
  }, [planning, bimesterProductMix, selectedBimesterId, activeDropCycle]);

  useEffect(() => {
    if (committedLooks.length === 0) {
      if (activeLookIndex !== 0) {
        setActiveLookIndex(0);
      }
      return;
    }

    if (activeLookIndex >= committedLooks.length) {
      setActiveLookIndex(committedLooks.length - 1);
    }
  }, [committedLooks, activeLookIndex]);

  const handleLookTabChange = (_, newValue) => {
    setActiveLookIndex(newValue);
  };

  const productMixTargets = useMemo(() => {
    // Use bimester-specific productMix when available, otherwise fall back to global
    const sourceProductMix = bimesterProductMix.length > 0 ? bimesterProductMix : (planning?.productMix || []);

    if (!sourceProductMix || sourceProductMix.length === 0) return [];

    const shareSum = sourceProductMix.reduce(
      (sum, item) => sum + (Number.isFinite(item.plannedShare) ? Math.max(item.plannedShare, 0) : 0),
      0
    );

    const categoryCount = sourceProductMix.length || 0;
    const totalLooksTarget =
      planning?.summary?.plannedLooks || planning?.summary?.productCount || categoryCount || 0;
    const totalUnitsTarget = planning?.summary?.projectedUnits || 0;

    return sourceProductMix.map((item, index) => {
      const rawShare = Number.isFinite(item.plannedShare) ? Math.max(item.plannedShare, 0) : 0;
      const shareAllocation =
        shareSum > 0 ? rawShare / (shareSum || 1) : categoryCount > 0 ? 1 / categoryCount : 0;

      const plannedLooks = Number.isFinite(item.plannedLooks)
        ? Math.max(item.plannedLooks, 0)
        : Math.max(0, Math.round(totalLooksTarget * shareAllocation));

      const plannedUnits = Number.isFinite(item.plannedUnits)
        ? Math.max(item.plannedUnits, 0)
        : Math.max(0, Math.round(totalUnitsTarget * shareAllocation));

       const handleLookDropdownChange = (event) => {
        const nextLookId = event.target.value;
        const nextIndex = committedLooks.findIndex((look) => look.id === nextLookId);
        if (nextIndex >= 0) {
          setActiveLookIndex(nextIndex);           
        }
       };  

      const categoryRevenue = (planning?.summary?.plannedRevenue || revenueGoal) * shareAllocation;
      const categoryCogsBudget = totalCogsBudget * shareAllocation;
      const perLookRetail = plannedLooks > 0 ? categoryRevenue / plannedLooks : 0;
      const perLookCogs = plannedLooks > 0 ? categoryCogsBudget / plannedLooks : 0;
      const perUnitRevenue = plannedUnits > 0 ? categoryRevenue / plannedUnits : 0;
      const perUnitCogs = plannedUnits > 0 ? categoryCogsBudget / plannedUnits : 0;
      const materialsBudgetPerLook = perLookCogs * materialsShare;
      const laborBudgetPerLook = perLookCogs * laborShare;
      const materialsBudgetPerUnit = perUnitCogs * materialsShare;
      const laborBudgetPerUnit = perUnitCogs * laborShare;
      const materialsBudgetTotal = categoryCogsBudget * materialsShare;
      const laborBudgetTotal = categoryCogsBudget * laborShare;

      return {
        ...item,
        share: rawShare,
        shareAllocation,
        plannedLooks,
        plannedUnits,
        categoryRevenue,
        categoryCogsBudget,
        perLookRetail,
        perLookCogs,
        perUnitRevenue,
        perUnitCogs,
        materialsBudgetPerLook,
        laborBudgetPerLook,
        materialsBudgetPerUnit,
        laborBudgetPerUnit,
        materialsBudgetTotal,
        laborBudgetTotal,
        categoryKey: item.categoryId || item.categoryLabel || `category-${index}`
      };
    });
  }, [
    bimesterProductMix,
    planning?.productMix,
    planning?.summary?.plannedRevenue,
    planning?.summary?.plannedLooks,
    planning?.summary?.productCount,
    planning?.summary?.projectedUnits,
    revenueGoal,
    totalCogsBudget,
    materialsShare,
    laborShare
  ]);

  const productCount = planning?.summary?.plannedLooks || productMixTargets.reduce((sum, item) => sum + (item.plannedLooks || 0), 0) || planning?.productMix?.length || 0;

  const categoryDetailLookup = useMemo(() => {
    const map = new Map();
    productMixTargets.forEach((category, index) => {
      const key = category.categoryKey || category.categoryId || category.categoryLabel || `category-${index}`;
      map.set(key, category);
    });
    return map;
  }, [productMixTargets]);

  const safeLookIndex = committedLooks.length > 0 ? Math.min(activeLookIndex, committedLooks.length - 1) : 0;
  const selectedLook = committedLooks.length > 0 ? committedLooks[safeLookIndex] : null;
  const selectedCategoryDetail = selectedLook ? categoryDetailLookup.get(selectedLook.categoryKey) || null : null;
  const hasCommittedLooks = committedLooks.length > 0;
  const activeLookName = selectedLook ? selectedLook.categoryLabel || `Look ${selectedLook.sequence}` : '';
  const activeLookId = selectedLook ? selectedLook.id : null;

  useEffect(() => {
    if (!heroHeaderRef.current) {
      return;
    }
    const height = heroHeaderRef.current.getBoundingClientRect().height || 0;
    setHeroHeaderHeight(height);
  }, [selectedLook]);

  const filteredAssetLibrary = useMemo(() => {
    if (!activeLookId) {
      return [];
    }

    return assetLibrary.filter((asset) => {
      const metaLookId = asset.meta?.lookId ?? asset.lookId ?? null;
      return metaLookId === activeLookId || metaLookId === null;
    });
  }, [assetLibrary, activeLookId]);

  const productHeroImage = useMemo(() => {
    const library = dropConfig?.assets?.library;
    if (!Array.isArray(library) || library.length === 0) {
      return null;
    }
    const preferred = library.find((asset) => {
      const category = asset?.category?.toLowerCase?.() || '';
      return category.includes('design') && asset?.url;
    });
    const fallback = library.find((asset) => asset?.url);
    return preferred?.url || fallback?.url || null;
  }, [dropConfig?.assets?.library]);

  const activeSchema = useMemo(() => {
    if (!dropConfig) {
      return getDefaultSchemaForMode('wholesale');
    }
    const modeKey = getSchemaKeyForMode(dropConfig.mode);
    const schemas = dropConfig.productInfo?.schemas || {};
    return schemas[modeKey] || getDefaultSchemaForMode(dropConfig.mode || 'wholesale');
  }, [dropConfig]);

  // Reset image carousel when look changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [activeLookId]);

  useEffect(() => {
    if (!dropConfig || !dropConfig.lookId) {
      if (assetLibrary.length > 0) {
        setAssetLibrary([]);
      }
      return;
    }

    if (dropConfig.lookId !== activeLookId) {
      return;
    }

    const library = Array.isArray(dropConfig.assets?.library) ? dropConfig.assets.library : [];
    const normalized = library
      .map((item) => normalizeAssetEntry(item, dropConfig.lookId))
      .filter(Boolean);

    const isSame =
      assetLibrary.length === normalized.length &&
      assetLibrary.every((entry, index) => {
        const next = normalized[index];
        if (!next) return false;
        return (
          entry.id === next.id &&
          entry.name === next.name &&
          entry.category === next.category &&
          entry.sourceType === next.sourceType &&
          entry.url === next.url &&
          (entry.lookId || null) === (next.lookId || null) &&
          entry.createdAt === next.createdAt &&
          entry.updatedAt === next.updatedAt &&
          JSON.stringify(entry.meta || {}) === JSON.stringify(next.meta || {})
        );
      });

    if (!isSame) {
      setAssetLibrary(normalized);
    }
  }, [assetLibrary, dropConfig, activeLookId]);

  const updateDropCache = useCallback((lookId, updates) => {
    if (!lookId) return;
    const prevCache = dropStateCacheRef.current;
    const entry = prevCache[lookId] || {};
    dropStateCacheRef.current = {
      ...prevCache,
      [lookId]: {
        ...entry,
        ...updates,
      },
    };
  }, []);

  const patchDropConfig = useCallback(
    (updater) => {
      setDropConfig((prev) => {
        if (!prev) {
          return prev;
        }

        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (!next || next === prev) {
          return prev;
        }

        const lookId = next.lookId || prev.lookId || selectedLook?.id || null;
        const prevStatus = typeof prev.status === 'string' ? prev.status : 'draft';
        let incomingStatus = typeof next.status === 'string' ? next.status : prevStatus;
        const normalizedStatus = ['draft', 'ready'].includes(incomingStatus?.toLowerCase?.() || '')
          ? incomingStatus.toLowerCase()
          : prevStatus;
        const finalStatus =
          prevStatus === 'ready' && normalizedStatus === 'ready' && next !== prev ? 'draft' : normalizedStatus || 'draft';
        const normalizedNext = {
          ...next,
          status: finalStatus,
        };
        setDropDirty(true);
        if (lookId) {
          updateDropCache(lookId, {
            config: normalizedNext,
            budget: dropStateCacheRef.current[lookId]?.budget ?? dropBudget,
            planningStatus:
              dropStateCacheRef.current[lookId]?.planningStatus ?? dropPlanningStatus ?? planning?.status ?? null,
            dirty: true,
          });
        }
        return normalizedNext;
      });
    },
    [dropBudget, dropPlanningStatus, planning?.status, selectedLook?.id, updateDropCache]
  );

  const applyAssetLibrary = useCallback(
    (updater) => {
      setAssetLibrary((prev) => {
        const nextSource = typeof updater === 'function' ? updater(prev) : updater;
        const normalized = (Array.isArray(nextSource) ? nextSource : [])
          .map((item) => normalizeAssetEntry(item, activeLookId))
          .filter(Boolean);

        const isSame =
          prev.length === normalized.length &&
          prev.every((entry, index) => {
            const next = normalized[index];
            if (!next) return false;
            const metaEqual =
              JSON.stringify(entry.meta || {}) === JSON.stringify(next.meta || {});
            return (
              entry.id === next.id &&
              entry.name === next.name &&
              entry.category === next.category &&
              entry.sourceType === next.sourceType &&
              entry.url === next.url &&
              (entry.lookId || null) === (next.lookId || null) &&
              entry.createdAt === next.createdAt &&
              entry.updatedAt === next.updatedAt &&
              metaEqual
            );
          });

        if (dropConfig && !isSame) {
          patchDropConfig((prevConfig) => {
            if (!prevConfig) {
              return prevConfig;
            }
            const prevAssets = prevConfig.assets || {};
            return {
              ...prevConfig,
              assets: {
                ...prevAssets,
                library: normalized,
              },
            };
          });
        }

        return normalized;
      });
    },
    [activeLookId, dropConfig, patchDropConfig]
  );

  const requestDropConfig = useCallback(async () => {
    if (!brandId || !selectedLook) {
      throw new Error('Drop configuration requires an active brand and look.');
    }

    return fetchDropConfiguration(brandId, selectedLook.id, {
      categoryKey: selectedLook.categoryKey,
      categoryLabel: selectedLook.categoryLabel,
      sequence: selectedLook.sequence,
    });
  }, [brandId, selectedLook]);

  const dropNavKeys = useMemo(
    () => (dropOnlyMode ? ['budgetTracker'] : ['budgetTracker', 'productInfo']),
    [dropOnlyMode]
  );

  useEffect(() => {
    if (!dropNavKeys.includes(activeNav) || !brandId || !selectedLook) {
      return;
    }

    const cachedEntry = dropStateCacheRef.current[selectedLook.id];
    if (cachedEntry?.config) {
      setDropConfig(cachedEntry.config);
      setDropBudget(cachedEntry.budget ?? null);
      setDropPlanningStatus(cachedEntry.planningStatus ?? planning?.status ?? null);
      setDropDirty(Boolean(cachedEntry.dirty));
    } else {
      setDropConfig(null);
      setDropBudget(null);
      setDropPlanningStatus(planning?.status || null);
      setDropDirty(false);
    }

    let isActive = true;

    const loadDropConfig = async () => {
      setDropLoading(true);
      setDropError(null);

      try {
        const response = await requestDropConfig();

        if (!isActive) {
          return;
        }

        const freshConfig = response?.config || null;
        const freshBudget = response?.budget || null;
        const freshStatus = response?.planningStatus || planning?.status || null;
        const cached = dropStateCacheRef.current[selectedLook.id];

        if (cached?.dirty && cached?.config) {
          setDropConfig(cached.config);
          setDropBudget(freshBudget ?? cached.budget ?? null);
          setDropPlanningStatus(freshStatus ?? cached.planningStatus ?? null);
          setDropDirty(true);
          dropStateCacheRef.current = {
            ...dropStateCacheRef.current,
            [selectedLook.id]: {
              ...cached,
              budget: freshBudget ?? cached.budget ?? null,
              planningStatus: freshStatus ?? cached.planningStatus ?? null,
            },
          };
        } else {
          // Auto-assign to current bimester if no dropCycle is set
          let needsSave = false;
          if (freshConfig && !freshConfig.dropCycle && (selectedBimesterId || activeDropCycle?.cycleId)) {
            freshConfig.dropCycle = selectedBimesterId || activeDropCycle?.cycleId || '';
            needsSave = true; // Mark as dirty so it gets saved
          }

          // Always sync the header selector to the loaded look's actual dropCycle.
          // Without this, selectedBimesterId can drift from dropConfig.dropCycle
          // (e.g. user picks B2 in header, then opens a B1 look → UI shows B2 but saves B1).
          if (freshConfig?.dropCycle) {
            setSelectedBimesterId(freshConfig.dropCycle);
          }

          setDropConfig(freshConfig);
          setDropBudget(freshBudget);
          setDropPlanningStatus(freshStatus);
          setDropDirty(needsSave);
        }
      } catch (error) {
        if (!isActive) {
          return;
        }
        console.error('Failed to load drop configuration', error);
        setDropError(error.response?.data?.message || error.message || 'Unable to load drop configuration');
        if (!cachedEntry?.config) {
          setDropConfig(null);
          setDropBudget(null);
          setDropDirty(false);
        }
      } finally {
        if (isActive) {
          setDropLoading(false);
        }
      }
    };

    loadDropConfig();

    return () => {
      isActive = false;
    };
  }, [activeNav, brandId, dropNavKeys, planning?.status, requestDropConfig, selectedLook]);

  useEffect(() => {
    if (!activeLookId) {
      setCurrentPrompt('');
      setNegativePrompt(DEFAULT_NEGATIVE_PROMPT);
      setNumImages(1);
      setQualitySteps(25);
      setGuidanceScale(7.5);
      setGeneratedImages([]);
      setGraphicForm(DEFAULT_GRAPHIC_FORM);
      return;
    }

    const stored = lookPromptState[activeLookId];
    if (stored) {
      setCurrentPrompt(stored.prompt ?? '');
      setNegativePrompt(stored.negativePrompt ?? DEFAULT_NEGATIVE_PROMPT);
      setNumImages(Number.isFinite(stored.numImages) ? stored.numImages : 1);
      setQualitySteps(Number.isFinite(stored.qualitySteps) ? stored.qualitySteps : 25);
      setGuidanceScale(Number.isFinite(stored.guidanceScale) ? stored.guidanceScale : 7.5);
      setGraphicForm(stored.graphicForm ? { ...DEFAULT_GRAPHIC_FORM, ...stored.graphicForm } : DEFAULT_GRAPHIC_FORM);
    } else {
      setCurrentPrompt('');
      setNegativePrompt(DEFAULT_NEGATIVE_PROMPT);
      setNumImages(1);
      setQualitySteps(25);
      setGuidanceScale(7.5);
      setGraphicForm(DEFAULT_GRAPHIC_FORM);
    }
    setGeneratedImages(Array.isArray(lookGeneratedImages[activeLookId]) ? lookGeneratedImages[activeLookId] : []);
  }, [activeLookId, lookPromptState, lookGeneratedImages]);

  useEffect(() => {
    if (!activeLookId) {
      return;
    }

    updateLookPromptState(activeLookId, {
      prompt: currentPrompt,
      negativePrompt,
      numImages,
      qualitySteps,
      guidanceScale,
      graphicForm,
    });
  }, [activeLookId, currentPrompt, negativePrompt, numImages, qualitySteps, guidanceScale, graphicForm, updateLookPromptState]);

  const selectedSupplier = useMemo(() => {
    if (!dropConfig?.wholesale?.supplierId) return null;
    return wholesaleCatalog.find((supplier) => supplier.supplierId === dropConfig.wholesale.supplierId) || null;
  }, [dropConfig?.wholesale?.supplierId, wholesaleCatalog]);

  const supplierCatalogItems = useMemo(() => {
    return Array.isArray(selectedSupplier?.catalog) ? selectedSupplier.catalog : [];
  }, [selectedSupplier]);

  const selectedCatalogItem = useMemo(() => {
    if (!dropConfig?.wholesale?.catalogItemId || !selectedSupplier) return null;
    return supplierCatalogItems.find((item) => item.id === dropConfig.wholesale.catalogItemId) || null;
  }, [dropConfig?.wholesale?.catalogItemId, supplierCatalogItems, selectedSupplier]);

  const selectedPattern = useMemo(() => {
    if (!dropConfig?.cutAndSew?.patternId) return null;
    return patternCatalog.find((pattern) => pattern.id === dropConfig.cutAndSew.patternId) || null;
  }, [dropConfig?.cutAndSew?.patternId, patternCatalog]);

  const fabricOptions = useMemo(() => {
    const optionsMap = new Map();
    const options = [];

    if (Array.isArray(selectedPattern?.recommendedFabric)) {
      selectedPattern.recommendedFabric.forEach((fabric) => {
        if (fabric?.id && !optionsMap.has(fabric.id)) {
          optionsMap.set(fabric.id, { ...fabric, source: 'recommended' });
          options.push({ ...fabric, source: 'recommended' });
        }
      });
    }

    fabricCatalog.forEach((fabric) => {
      if (fabric?.id && !optionsMap.has(fabric.id)) {
        optionsMap.set(fabric.id, { ...fabric, source: 'library' });
        options.push({ ...fabric, source: 'library' });
      }
    });

    return options;
  }, [selectedPattern, fabricCatalog]);

  const dropEstimates = useMemo(() => {
    if (!dropConfig) {
      return {
        units: 0,
        perUnitCost: 0,
        totalCost: 0,
        budget: Number(dropBudget?.totalCogsBudget) || 0,
        remaining: Number(dropBudget?.totalCogsBudget) || 0,
        withinBudget: true,
      };
    }

    const units = Number(dropConfig.units) || 0;
    let perUnitCost = 0;

    if (dropConfig.mode === 'wholesale') {
      perUnitCost = Number(dropConfig.wholesale?.unitCost) || 0;
    } else {
      const fabricCost = Number(dropConfig.cutAndSew?.fabricCostPerUnit) || 0;
      const laborCost = Number(dropConfig.cutAndSew?.laborCostPerUnit) || 0;
      const trimsCost = Number(dropConfig.cutAndSew?.trimsCostPerUnit) || 0;
      perUnitCost = fabricCost + laborCost + trimsCost;
    }

    const totalCost = perUnitCost * units;
    const budget = Number(dropBudget?.totalCogsBudget) || 0;
    const remaining = budget - totalCost;

    return {
      units,
      perUnitCost,
      totalCost,
      budget,
      remaining,
      withinBudget: Number.isFinite(remaining) ? remaining >= 0 : true,
    };
  }, [dropConfig, dropBudget]);

  const dropBudgetChips = useMemo(() => {
    if (!dropBudget) {
      return [];
    }

    const chips = [];

    if (Number.isFinite(dropBudget.totalCogsBudget)) {
      chips.push(`COGS Budget ${formatCurrency(dropBudget.totalCogsBudget)}`);
    }
    if (Number.isFinite(dropBudget.plannedUnits)) {
      chips.push(`Planned Units ${(dropBudget.plannedUnits || 0).toLocaleString()}`);
    }
    if (Number.isFinite(dropBudget.perUnitCogs)) {
      chips.push(`Per Unit Budget ${formatCurrency(dropBudget.perUnitCogs || 0)}`);
    }
    if (Number.isFinite(dropBudget.perLookCogs)) {
      chips.push(`Per Look Budget ${formatCurrency(dropBudget.perLookCogs || 0)}`);
    }
    if (Number.isFinite(dropBudget.materialsShare)) {
      chips.push(`Materials ${formatPercent(dropBudget.materialsShare)}`);
    }
    if (Number.isFinite(dropBudget.laborShare)) {
      chips.push(`Labor ${formatPercent(dropBudget.laborShare)}`);
    }

    return chips;
  }, [dropBudget]);

  const isLookSaved = useCallback(
    (lookId) => {
      if (!lookId) return false;
      const cached = dropStateCacheRef.current[lookId];
      if (cached) {
        if (cached.dirty) {
          return false;
        }
        if (cached.config && cached.config.status === 'ready') {
          return true;
        }
      }
      const configs = Array.isArray(planning?.lookConfigurations) ? planning.lookConfigurations : [];
      return configs.some((config) => config?.lookId === lookId && config?.status === 'ready');
    },
    [planning?.lookConfigurations]
  );

  const lookSummaryChips = useMemo(() => {
    if (!selectedLook) return null;

    const entries = [`Category: ${activeLookName}`];

    if (selectedCategoryDetail && Number.isFinite(selectedCategoryDetail.plannedLooks)) {
      entries.push(`Planned Looks: ${selectedCategoryDetail.plannedLooks}`);
    }
    if (selectedCategoryDetail && Number.isFinite(selectedCategoryDetail.plannedUnits)) {
      entries.push(`Planned Units: ${(selectedCategoryDetail.plannedUnits || 0).toLocaleString()}`);
    }
    if (selectedCategoryDetail && Number.isFinite(selectedCategoryDetail.categoryCogsBudget)) {
      entries.push(`COGS Budget: ${formatCurrency(selectedCategoryDetail.categoryCogsBudget)}`);
    }
    if (selectedCategoryDetail && Number.isFinite(selectedCategoryDetail.perLookCogs)) {
      entries.push(`Per Look COGS: ${formatCurrency(selectedCategoryDetail.perLookCogs)}`);
    }
    if (selectedCategoryDetail && Number.isFinite(selectedCategoryDetail.perUnitCogs)) {
      entries.push(`Per Unit COGS: ${formatCurrency(selectedCategoryDetail.perUnitCogs)}`);
    }
    if (selectedCategoryDetail && Number.isFinite(selectedCategoryDetail.perLookRetail)) {
      entries.push(`Per Look Retail: ${formatCurrency(selectedCategoryDetail.perLookRetail)}`);
    }
    if (selectedCategoryDetail && Number.isFinite(selectedCategoryDetail.perUnitRevenue)) {
      entries.push(`Per Unit Retail: ${formatCurrency(selectedCategoryDetail.perUnitRevenue)}`);
    }

    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" alignItems="center">
        {entries.map((label) => (
          <Chip key={label} label={label} size="small" />
        ))}
      </Stack>
    );
  }, [selectedLook, selectedCategoryDetail, activeLookName]);

  const lookWorkspaceMessage = useMemo(() => {
    if (!selectedLook) return null;

    const notes = selectedCategoryDetail?.notes
      ? (
          <Typography variant="caption" color="text.secondary">
            Notes: {selectedCategoryDetail.notes}
          </Typography>
        )
      : null;

    if (activeNav === 'budgetTracker') {
      return (
        <Stack spacing={0.5} alignItems="flex-start">
          {notes}
          <Typography variant="body2" color="text.secondary">
            Capture sourcing inputs, tech pack requirements, and BOM cost assumptions for <strong>{activeLookName}</strong>.
          </Typography>
        </Stack>
      );
    }

    if (activeNav === 'marketing') {
      return (
        <Stack spacing={0.5} alignItems="flex-start">
          {notes}
          <Typography variant="body2" color="text.secondary">
            Develop marketing visuals, campaign copy, and hero imagery for <strong>{activeLookName}</strong>.
          </Typography>
        </Stack>
      );
    }

    if (activeNav === 'graphic') {
      return (
        <Stack spacing={0.5} alignItems="flex-start">
          {notes}
          <Typography variant="body2" color="text.secondary">
            Iterate on garment graphics, prints, and placement concepts for <strong>{activeLookName}</strong>.
          </Typography>
        </Stack>
      );
    }

    if (activeNav === 'productInfo') {
      return (
        <Stack spacing={0.5} alignItems="flex-start">
          {notes}
          <Typography variant="body2" color="text.secondary">
            Capture product naming, SKU structure, material details, and technical notes for <strong>{activeLookName}</strong>.
          </Typography>
        </Stack>
      );
    }

    if (activeNav === 'lookbook') {
      return (
        <Stack spacing={0.5} alignItems="flex-start">
          {notes}
          <Typography variant="body2" color="text.secondary">
            Plan hero shots and storytelling beats for <strong>{activeLookName}</strong>. Layout tooling arrives soon.
          </Typography>
        </Stack>
      );
    }

    if (activeNav === 'archive') {
      return (
        <Stack spacing={0.5} alignItems="flex-start">
          {notes}
          <Typography variant="body2" color="text.secondary">
            Final renders, prompts, and production receipts for <strong>{activeLookName}</strong> will live here once archiving switches on.
          </Typography>
        </Stack>
      );
    }

    return null;
  }, [selectedLook, selectedCategoryDetail, activeNav, activeLookName]);

  const categoryUnitsGuardrail = useMemo(() => {
    if (!selectedCategoryDetail || !selectedLook) {
      return null;
    }

    const normalizeKey = (value) =>
      typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, '-') : '';

    const plannedUnitsRaw = Number(selectedCategoryDetail.plannedUnits);
    const plannedUnits = Number.isFinite(plannedUnitsRaw) && plannedUnitsRaw > 0 ? plannedUnitsRaw : 0;
    const normalizedCategoryKey =
      normalizeKey(selectedLook.categoryKey) || normalizeKey(selectedLook.categoryLabel);
    const totalLooksForCategory =
      committedLooks.filter(
        (look) => (normalizeKey(look.categoryKey) || normalizeKey(look.categoryLabel)) === normalizedCategoryKey
      ).length || 1;

    const allConfigs = Array.isArray(planning?.lookConfigurations) ? [...planning.lookConfigurations] : [];
    const overrideConfigs = Object.values(dropStateCacheRef.current || {})
      .map((entry) => entry?.config)
      .filter((config) => config && typeof config === 'object');

    overrideConfigs.forEach((config) => {
      const lookId = config.lookId;
      if (!lookId) return;
      const idx = allConfigs.findIndex((item) => item?.lookId === lookId);
      if (idx >= 0) {
        allConfigs[idx] = {
          ...allConfigs[idx],
          ...config,
        };
      } else {
        allConfigs.push(config);
      }
    });

    const currentLookId = dropConfig?.lookId || selectedLook.id;

    const normalizedConfigs = allConfigs
      .filter((config) => {
        if (!config) return false;
        const configKey = normalizeKey(config.categoryKey) || normalizeKey(config.categoryLabel);
        const matchesCategory = configKey === normalizedCategoryKey;
        // Also filter by bimester - only count looks in the current bimester
        const matchesBimester = !activeDropCycle?.cycleId || config.dropCycle === activeDropCycle.cycleId;
        return matchesCategory && matchesBimester;
      })
      .map((config) => ({
        lookId: config.lookId,
        units: Math.max(0, Number(config.units) || 0),
        unitsLocked: Boolean(config.unitsLocked),
      }));

    const currentUnitsRaw = Number(dropConfig?.units);
    const currentUnits = Number.isFinite(currentUnitsRaw) && currentUnitsRaw > 0 ? currentUnitsRaw : 0;
    const currentIsLocked = Boolean(dropConfig?.unitsLocked);

    const existingIndex = normalizedConfigs.findIndex((config) => config.lookId === currentLookId);
    if (existingIndex >= 0) {
      normalizedConfigs[existingIndex] = {
        ...normalizedConfigs[existingIndex],
        units: currentUnits,
        unitsLocked: currentIsLocked,
      };
    } else {
      normalizedConfigs.push({
        lookId: currentLookId,
        units: currentUnits,
        unitsLocked: currentIsLocked,
      });
    }

    const lockedUnits = normalizedConfigs
      .filter((config) => config.unitsLocked)
      .reduce((sum, config) => sum + config.units, 0);

    const unlockedConfigs = normalizedConfigs.filter((config) => !config.unitsLocked);
    const unlockedCount = unlockedConfigs.length;
    const otherUnlockedUnits = unlockedConfigs
      .filter((config) => config.lookId !== currentLookId)
      .reduce((sum, config) => sum + config.units, 0);

    const otherUnits = normalizedConfigs
      .filter((config) => config.lookId !== currentLookId)
      .reduce((sum, config) => sum + config.units, 0);

    const remainingForUnlocked = plannedUnits > 0 ? Math.max(0, plannedUnits - lockedUnits) : 0;
    const sharePerUnlocked = unlockedCount > 0 ? remainingForUnlocked / unlockedCount : 0;
    const maxAssignable = Math.max(0, remainingForUnlocked - otherUnlockedUnits);
    const availableShare = Math.min(maxAssignable, sharePerUnlocked);
    // Allow up to maxAssignable (all remaining units), not just fair share
    const allowedUpper = currentIsLocked ? currentUnits : Math.max(currentUnits, maxAssignable);
    const remainingForOthers = plannedUnits > 0 ? Math.max(0, plannedUnits - otherUnits - currentUnits) : 0;

    const baselinePerLook =
      plannedUnits > 0 && totalLooksForCategory > 0 ? plannedUnits / totalLooksForCategory : plannedUnits;

    const totalAssigned = normalizedConfigs.reduce((sum, config) => sum + config.units, 0);

    return {
      plannedUnits,
      totalLooksForCategory,
      baselinePerLook,
      otherUnits,
      maxAssignable,
      sharePerUnlocked,
      availableShare,
      allowedUpper,
      maxForCurrent: allowedUpper,
      currentUnits,
      remainingForOthers,
      totalAssigned,
      lockedUnits,
      unlockedCount,
      otherUnlockedUnits,
      currentIsLocked,
    };
  }, [
    activeDropCycle?.cycleId,
    committedLooks,
    dropConfig?.lookId,
    dropConfig?.units,
    dropConfig?.unitsLocked,
    planning?.lookConfigurations,
    selectedCategoryDetail,
    selectedLook,
  ]);

  useEffect(() => {
    if (!categoryUnitsGuardrail) {
      setUnitGuardrailWarning(null);
      return;
    }
    if (categoryUnitsGuardrail.currentUnits <= categoryUnitsGuardrail.maxForCurrent) {
      setUnitGuardrailWarning(null);
    }
  }, [categoryUnitsGuardrail]);

  useEffect(() => {
    if (!dropConfig?.lookId) {
      return;
    }
    const lookId = dropConfig.lookId;
    const entry = dropStateCacheRef.current[lookId] || {};
    if (
      entry.config === dropConfig &&
      entry.budget === dropBudget &&
      entry.planningStatus === dropPlanningStatus &&
      entry.dirty === dropDirty
    ) {
      return;
    }
    dropStateCacheRef.current = {
      ...dropStateCacheRef.current,
      [lookId]: {
        ...entry,
        config: dropConfig,
        budget: dropBudget,
        planningStatus: dropPlanningStatus,
        dirty: dropDirty,
      },
    };
  }, [dropConfig, dropBudget, dropDirty, dropPlanningStatus]);

  useEffect(() => {
    if (!dropConfig?.dropCycle) {
      return;
    }

    const cycle = dropCycleOptions.find((entry) => entry.cycleId === dropConfig.dropCycle);
    if (!cycle) {
      return;
    }

    const updates = buildCycleAutoUpdates(cycle, dropConfig.productInfo || {});
    if (!updates || Object.keys(updates).length === 0) {
      return;
    }

    patchDropConfig((prev) => {
      if (!prev || prev.dropCycle !== dropConfig.dropCycle) {
        return prev;
      }

      const innerUpdates = buildCycleAutoUpdates(cycle, prev.productInfo || {});
      if (!innerUpdates || Object.keys(innerUpdates).length === 0) {
        return prev;
      }

      return {
        ...prev,
        productInfo: {
          ...(prev.productInfo || {}),
          ...innerUpdates,
        },
      };
    });
  }, [dropConfig?.dropCycle, dropConfig?.productInfo, dropCycleOptions, patchDropConfig]);

  useEffect(() => {
    if (!dropConfig) {
      return;
    }
    const schemas = dropConfig.productInfo?.schemas || {};
    if (schemas.cutAndSew && schemas.whiteLabel) {
      return;
    }
    patchDropConfig((prev) => {
      if (!prev) return prev;
      const info = prev.productInfo || {};
      const prevSchemas = info.schemas || {};
      return {
        ...prev,
        productInfo: {
          ...info,
          schemas: {
            cutAndSew: prevSchemas.cutAndSew || createDefaultCutAndSewSchema(),
            whiteLabel: prevSchemas.whiteLabel || createDefaultWhiteLabelSchema()
          }
        }
      };
    });
  }, [dropConfig, patchDropConfig]);

const unitsHelperText = useMemo(() => {
  if (unitGuardrailWarning?.message) {
    return unitGuardrailWarning.message;
  }
  if (categoryUnitsGuardrail && categoryUnitsGuardrail.plannedUnits > 0) {
    const {
      plannedUnits,
      totalAssigned,
      sharePerUnlocked,
      unlockedCount,
    } = categoryUnitsGuardrail;

    const remaining = plannedUnits - totalAssigned;
    const suggested = unlockedCount > 0 ? Math.round(sharePerUnlocked) : 0;

    return `Suggested: ${formatUnits(suggested)} units • Category Total: ${formatUnits(plannedUnits)} • Used: ${formatUnits(totalAssigned)} • Remaining: ${formatUnits(remaining)}`;
  }
  return 'Enter the number of units you plan to produce for this look.';
}, [categoryUnitsGuardrail, unitGuardrailWarning]);

const handleUnitsLockToggle = useCallback(
  (event) => {
    const checked = event.target.checked;
    patchDropConfig((prev) => ({
      ...prev,
      unitsLocked: checked,
    }));
    setUnitGuardrailWarning(null);
  },
  [patchDropConfig]
);

  const handleDropRefresh = useCallback(async () => {
    if (dropLoading || !selectedLook) {
      return;
    }

    try {
      setDropLoading(true);
      setDropError(null);

      const response = await requestDropConfig();
      setDropConfig(response?.config || null);
      setDropBudget(response?.budget || null);
      setDropPlanningStatus(response?.planningStatus || planning?.status || null);
      setDropDirty(false);
      toast.success('Drop configuration refreshed');
    } catch (error) {
      console.error('Failed to refresh drop configuration', error);
      setDropError(error.response?.data?.message || error.message || 'Unable to refresh drop configuration');
    } finally {
      setDropLoading(false);
    }
  }, [dropLoading, requestDropConfig, planning?.status, selectedLook]);

  const handleDropUnitsChange = useCallback(
    (event) => {
      const parsed = Number(event.target.value);
      const sanitized = Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;

      if (!dropConfig) {
        return;
      }

      if (!categoryUnitsGuardrail || categoryUnitsGuardrail.plannedUnits === 0) {
        setUnitGuardrailWarning(null);
        patchDropConfig((prev) => ({
          ...prev,
          units: sanitized,
        }));
        return;
      }

    const allowed = categoryUnitsGuardrail.allowedUpper ?? sanitized;
    const shareTarget = categoryUnitsGuardrail.sharePerUnlocked ?? null;
    const availableShare = categoryUnitsGuardrail.availableShare ?? shareTarget ?? allowed;
    const nextUnits = Math.min(sanitized, allowed);
    const exceeded = sanitized > allowed + 1e-6; // allow tiny float tolerance

    patchDropConfig((prev) => ({
      ...prev,
      units: nextUnits,
    }));

    if (exceeded) {
      const shareMessage =
        shareTarget !== null
          ? `Balanced share is ${formatUnits(availableShare)} units across unlocked looks.`
          : '';
      const message = `Only ${formatUnits(
        allowed
      )} units available for this look. ${shareMessage} Adjust other looks or planner targets to redistribute.`;
      if (!unitGuardrailWarning || unitGuardrailWarning.allowed !== allowed || unitGuardrailWarning.message !== message) {
        toast.error('Category unit guardrail reached. Adjust allocations or lock selections.');
      }
      setUnitGuardrailWarning({
        attempted: sanitized,
        allowed,
        message,
        });
      } else {
        setUnitGuardrailWarning(null);
      }
    },
    [categoryUnitsGuardrail, dropConfig, patchDropConfig, unitGuardrailWarning]
  );

  const handleDropModeChange = useCallback(
    (event) => {
      const value = event.target.value;
      // Support wholesale, cut_and_sew, and knitup modes
      const normalized = ['cut_and_sew', 'knitup'].includes(value) ? value : 'wholesale';
      patchDropConfig((prev) => {
        if (prev.mode === normalized) {
          return prev;
        }
        return {
          ...prev,
          mode: normalized,
        };
      });
    },
    [patchDropConfig]
  );

  const handleDropCycleSelect = useCallback(
    (event) => {
      const cycleId = event.target.value || '';
      const cycle = dropCycleOptions.find((entry) => entry.cycleId === cycleId) || null;

      patchDropConfig((prev) => {
        if (!prev) {
          return prev;
        }

        const updates = cycle ? buildCycleAutoUpdates(cycle, prev.productInfo || {}) : {};
        const hasUpdates = updates && Object.keys(updates).length > 0;

        return {
          ...prev,
          dropCycle: cycleId,
          productInfo: hasUpdates
            ? {
                ...(prev.productInfo || {}),
                ...updates,
              }
            : { ...(prev.productInfo || {}) },
        };
      });
    },
    [dropCycleOptions, patchDropConfig]
  );

  // Handler for Kam Hero Header bimester selector - updates global selection AND current look
  const handleBimesterChange = useCallback(
    (event) => {
      const cycleId = event.target.value || '';

      // Update global bimester selection (affects header metadata)
      setSelectedBimesterId(cycleId);

      // Always attempt to sync the current look's dropCycle.
      // patchDropConfig is null-safe (no-op when dropConfig is null).
      handleDropCycleSelect(event);
    },
    [handleDropCycleSelect]
  );

  const handleResetLooks = useCallback(
    async (resetAll = false) => {
      if (!window.confirm(resetAll ? 'Reset ALL looks across all bimesters? This cannot be undone.' : `Reset looks for the current bimester? This cannot be undone.`)) {
        return;
      }

      try {
        const response = await axios.post(`/api/brands/${brandId}/planning/reset-looks`, {
          bimesterId: resetAll ? null : (selectedBimesterId || activeDropCycle?.cycleId)
        });

        if (response.data?.success) {
          toast.success(response.data.message || 'Looks reset successfully');
          // Reload planning data
          const planningData = await fetchBrandPlanning(brandId, { includeHistory: false });
          const normalizedPlanning = planningData
            ? {
                ...planningData,
                lookConfigurations: Array.isArray(planningData.lookConfigurations)
                  ? planningData.lookConfigurations
                  : [],
              }
            : planningData;
          setPlanning(normalizedPlanning);
        }
      } catch (error) {
        console.error('Failed to reset looks:', error);
        toast.error('Failed to reset looks');
      }
    },
    [brandId, selectedBimesterId, activeDropCycle]
  );

  const handleWholesaleSupplierChange = useCallback(
    (event) => {
      const supplierId = event.target.value;
      const supplier = wholesaleCatalog.find((item) => item.supplierId === supplierId) || null;

      patchDropConfig((prev) => {
        const resetNotes = prev.wholesale?.catalogItemId ? '' : prev.wholesale?.notes || '';
        const nextWholesale = {
          supplierId: supplierId || '',
          supplierName: supplier?.name || '',
          catalogItemId: '',
          catalogItemName: '',
          leadTimeDays: supplier?.leadTimeDays ?? null,
          unitCost: supplier?.catalog?.[0]?.unitCost ?? null,
          colorway: '',
          sizeScale: '',
          notes: resetNotes,
        };

        return {
          ...prev,
          wholesale: nextWholesale,
        };
      });
    },
    [patchDropConfig, wholesaleCatalog]
  );

  const handleWholesaleItemChange = useCallback(
    (event) => {
      const itemId = event.target.value;
      patchDropConfig((prev) => {
        const supplier = wholesaleCatalog.find(
          (entry) => entry.supplierId === (prev.wholesale?.supplierId || '')
        );
        const catalogItem =
          supplier?.catalog?.find((item) => item.id === itemId) || null;

        const previousNotes = prev.wholesale?.notes || '';
        const shouldReplaceNotes = !previousNotes || prev.wholesale?.catalogItemId !== itemId;

        const nextWholesale = {
          ...(prev.wholesale || {}),
          catalogItemId: itemId,
          catalogItemName: catalogItem?.name || '',
          unitCost:
            catalogItem?.unitCost !== undefined
              ? catalogItem.unitCost
              : prev.wholesale?.unitCost ?? null,
          leadTimeDays:
            catalogItem?.leadTimeDays !== undefined
              ? catalogItem.leadTimeDays
              : supplier?.leadTimeDays ?? prev.wholesale?.leadTimeDays ?? null,
          sizeScale: catalogItem?.sizeScale || prev.wholesale?.sizeScale || '',
          notes: shouldReplaceNotes ? catalogItem?.notes || '' : previousNotes,
        };

        if (
          catalogItem?.colorways &&
          catalogItem.colorways.length > 0 &&
          prev.wholesale?.catalogItemId !== itemId
        ) {
          nextWholesale.colorway = catalogItem.colorways[0];
        }

        // Auto-populate the blank cost in productInfo.costs.whiteLabel
        const blankCost = catalogItem?.unitCost ?? 0;
        const existingCosts = prev.productInfo?.costs || {};
        const existingWhiteLabel = existingCosts.whiteLabel || {};

        const nextProductInfo = {
          ...(prev.productInfo || {}),
          costs: {
            ...existingCosts,
            whiteLabel: {
              ...existingWhiteLabel,
              blank: {
                ...(existingWhiteLabel.blank || {}),
                cost: blankCost,
                supplierId: supplier?.supplierId || prev.wholesale?.supplierId,
                supplierName: supplier?.supplierName || prev.wholesale?.supplierName,
                catalogItemId: itemId
              }
            }
          }
        };

        return {
          ...prev,
          wholesale: nextWholesale,
          productInfo: nextProductInfo
        };
      });
    },
    [patchDropConfig, wholesaleCatalog]
  );

  const handleCatalogItemSelected = useCallback(
    ({ supplier, catalogItem }) => {
      console.log('📦 Selected from catalog browser:', { supplier, catalogItem });

      patchDropConfig((prev) => {
        // Build wholesale configuration from selected catalog item
        const nextWholesale = {
          supplier: supplier.id, // ObjectId reference (NEW)
          catalogItemId: catalogItem.catalogItemId,
          // Legacy fields for backward compatibility
          supplierId: supplier.supplierCode,
          supplierName: supplier.companyName,
          catalogItemName: catalogItem.itemName,
          // Get cost from first tier or fallback
          unitCost: catalogItem.costTiers?.[0]?.unitCost || 0,
          leadTimeDays: catalogItem.leadTimeDays || null,
          // Use user-selected color or fall back to first available
          colorway: catalogItem.selectedColor || catalogItem.colors?.[0] || '',
          sizeScale: catalogItem.selectedSizes?.join(', ') || catalogItem.sizes?.join(', ') || '',
          notes: `${catalogItem.brand || ''} ${catalogItem.fabricWeight || ''} ${catalogItem.fabricComposition || ''}`.trim()
        };

        // Auto-populate the blank cost in productInfo.costs.whiteLabel
        const blankCost = catalogItem.costTiers?.[0]?.unitCost || 0;
        const existingCosts = prev.productInfo?.costs || {};
        const existingWhiteLabel = existingCosts.whiteLabel || {};

        console.log('💰 Auto-populating blank cost:', {
          blankCost,
          supplierName: supplier.companyName,
          catalogItemId: catalogItem.catalogItemId,
          catalogItem
        });

        const nextProductInfo = {
          ...(prev.productInfo || {}),
          costs: {
            ...existingCosts,
            whiteLabel: {
              ...existingWhiteLabel,
              blank: {
                ...(existingWhiteLabel.blank || {}),
                cost: blankCost,
                supplierId: supplier.supplierCode,
                supplierName: supplier.companyName,
                catalogItemId: catalogItem.catalogItemId
              }
            }
          }
        };

        console.log('📝 Updated productInfo.costs:', nextProductInfo.costs);

        return {
          ...prev,
          wholesale: nextWholesale,
          productInfo: nextProductInfo
        };
      });

      // Close the browser
      setCatalogBrowserOpen(false);
    },
    [patchDropConfig]
  );

  const handleWholesaleFieldChange = useCallback(
    (field) => (event) => {
      const rawValue = event.target.value;
      patchDropConfig((prev) => {
        const nextWholesale = {
          ...(prev.wholesale || {}),
        };

        if (field === 'unitCost') {
          if (rawValue === '') {
            nextWholesale.unitCost = null;
          } else {
            const parsed = Number(rawValue);
            nextWholesale.unitCost = Number.isFinite(parsed) ? parsed : nextWholesale.unitCost ?? 0;
          }
        } else if (field === 'leadTimeDays') {
          if (rawValue === '') {
            nextWholesale.leadTimeDays = null;
          } else {
            const parsed = Number(rawValue);
            nextWholesale.leadTimeDays = Number.isFinite(parsed)
              ? Math.max(Math.round(parsed), 0)
              : nextWholesale.leadTimeDays ?? null;
          }
        } else {
          nextWholesale[field] = rawValue;
        }

        return {
          ...prev,
          wholesale: nextWholesale,
        };
      });
    },
    [patchDropConfig]
  );

  const handlePatternSourceChange = useCallback(
    (event) => {
      const value = event.target.value === 'new' ? 'new' : 'existing';
      patchDropConfig((prev) => {
        const nextCutAndSew = {
          ...(prev.cutAndSew || {}),
          patternSource: value,
        };

        if (value === 'new') {
          nextCutAndSew.patternId = '';
          nextCutAndSew.patternName = '';
        }

        return {
          ...prev,
          cutAndSew: nextCutAndSew,
        };
      });
    },
    [patchDropConfig]
  );

  const handlePatternSelect = useCallback(
    (event) => {
      const patternId = event.target.value;
      const pattern = patternCatalog.find((entry) => entry.id === patternId) || null;

      patchDropConfig((prev) => {
        const recommendedFabric = Array.isArray(pattern?.recommendedFabric)
          ? pattern.recommendedFabric[0]
          : null;

        const recommendedFabricCost =
          recommendedFabric && pattern?.averageFabricUsageYards
            ? Number((recommendedFabric.costPerYard * pattern.averageFabricUsageYards).toFixed(2))
            : prev.cutAndSew?.fabricCostPerUnit ?? null;

        const nextCutAndSew = {
          ...(prev.cutAndSew || {}),
          patternId: patternId || '',
          patternName: pattern?.name || '',
        };

        if (recommendedFabric) {
          nextCutAndSew.fabricId = recommendedFabric.id;
          nextCutAndSew.fabricName = recommendedFabric.name || '';
          nextCutAndSew.fabricCostPerUnit = recommendedFabricCost;
        }

        if (pattern?.trimsBudget !== undefined) {
          nextCutAndSew.trimsCostPerUnit = pattern.trimsBudget;
        }

        return {
          ...prev,
          cutAndSew: nextCutAndSew,
        };
      });
    },
    [patchDropConfig, patternCatalog]
  );

  const handleFabricSelect = useCallback(
    (event) => {
      const fabricId = event.target.value;
      const fabric = fabricOptions.find((entry) => entry.id === fabricId) || null;

      patchDropConfig((prev) => {
        const nextCutAndSew = {
          ...(prev.cutAndSew || {}),
          fabricId: fabricId || '',
          fabricName: fabric?.name || '',
        };

        if (fabric?.costPerYard && selectedPattern?.averageFabricUsageYards) {
          const perUnitCost = fabric.costPerYard * selectedPattern.averageFabricUsageYards;
          nextCutAndSew.fabricCostPerUnit = Number(perUnitCost.toFixed(2));
        }

        return {
          ...prev,
          cutAndSew: nextCutAndSew,
        };
      });
    },
    [patchDropConfig, fabricOptions, selectedPattern]
  );

  const handleCutAndSewFieldChange = useCallback(
    (field) => (event) => {
      const rawValue = event.target.value;

      patchDropConfig((prev) => {
        const nextCutAndSew = {
          ...(prev.cutAndSew || {}),
        };

        if (['fabricCostPerUnit', 'laborCostPerUnit', 'trimsCostPerUnit'].includes(field)) {
          if (rawValue === '') {
            nextCutAndSew[field] = null;
          } else {
            const parsed = Number(rawValue);
            nextCutAndSew[field] = Number.isFinite(parsed) ? parsed : nextCutAndSew[field] ?? 0;
          }
        } else {
          nextCutAndSew[field] = rawValue;
        }

        return {
          ...prev,
          cutAndSew: nextCutAndSew,
        };
      });
    },
    [patchDropConfig]
  );

  const handleBudgetOverrideChange = useCallback(
    (field) => (event) => {
      const rawValue = event.target.value;

      patchDropConfig((prev) => {
        const nextOverrides = {
          ...(prev.budgetOverrides || {}),
        };

        if (rawValue === '' || rawValue === null) {
          delete nextOverrides[field];
        } else {
          const parsed = Number(rawValue);
          if (field === 'materialsShare' || field === 'laborShare') {
            const clamped = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), 1) : 0;
            nextOverrides[field] = clamped;
          } else {
            nextOverrides[field] = Number.isFinite(parsed) ? parsed : nextOverrides[field] ?? 0;
          }
        }

        return {
          ...prev,
          budgetOverrides: Object.keys(nextOverrides).length > 0 ? nextOverrides : undefined,
        };
      });
    },
    [patchDropConfig]
  );

  const handleDropNotesChange = useCallback(
    (event) => {
      const value = event.target.value;
      patchDropConfig((prev) => ({
        ...prev,
        notes: value,
      }));
    },
    [patchDropConfig]
  );

  const handleAssetsChange = useCallback(
    (field) => (eventOrValue) => {
      const value =
        typeof eventOrValue === 'string' ? eventOrValue : eventOrValue?.target?.value ?? '';

      patchDropConfig((prev) => {
        const nextAssets = {
          ...(prev.assets || {}),
        };

        if (field === 'referenceImages') {
          const parsed = value
            .split(/\r?\n|,/)
            .map((item) => item.trim())
            .filter(Boolean);
          nextAssets.referenceImages = parsed;
        } else {
          nextAssets[field] = value;
        }

        return {
          ...prev,
          assets: nextAssets,
        };
      });
    },
    [patchDropConfig]
  );

  const handleGraphicPreset = useCallback(
    (preset) => {
      setCurrentPrompt(preset);
      setNegativePrompt(DEFAULT_NEGATIVE_PROMPT);
      setGraphicForm((prev) => {
        if (prev.motif) {
          return prev;
        }
        const next = {
          ...prev,
          motif: preset,
        };
        if (activeLookId) {
          updateLookPromptState(activeLookId, {
            graphicForm: next,
          });
        }
        return next;
      });
      if (activeLookId) {
        updateLookPromptState(activeLookId, {
          prompt: preset,
          negativePrompt: DEFAULT_NEGATIVE_PROMPT,
          numImages,
          qualitySteps,
          guidanceScale,
        });
      }
      toast.success('Graphic prompt preset applied');
    },
    [activeLookId, guidanceScale, numImages, qualitySteps, updateLookPromptState]
  );

  const handleGraphicFieldChange = useCallback(
    (field) => (event) => {
      const value = event?.target?.value ?? '';
      setGraphicForm((prev) => {
        const next = {
          ...prev,
          [field]: value,
        };
        if (activeLookId) {
          updateLookPromptState(activeLookId, { graphicForm: next });
        }
        return next;
      });
    },
    [activeLookId, updateLookPromptState]
  );

  const handleGraphicSuggest = useCallback(() => {
    setGraphicForm((prev) => {
      const next = {
        ...prev,
        style: prev.style || getRandom(GRAPHIC_STYLES),
        placement: prev.placement || getRandom(GRAPHIC_PLACEMENTS),
        technique: prev.technique || getRandom(GRAPHIC_TECHNIQUES),
        palette: prev.palette || getRandom(GRAPHIC_PALETTES),
        motif: prev.motif || `Inspired by ${getRandom(GRAPHIC_STYLES).toLowerCase()} motifs`,
        finish: prev.finish || getRandom(GRAPHIC_FINISHES),
      };
      if (activeLookId) {
        updateLookPromptState(activeLookId, { graphicForm: next });
      }
      toast.success('Graphic suggestions applied');
      return next;
    });
  }, [activeLookId, updateLookPromptState]);

  const handleGraphicPromptBuild = useCallback(() => {
    const fields = {
      style: graphicForm.style?.trim(),
      placement: graphicForm.placement?.trim(),
      technique: graphicForm.technique?.trim(),
      palette: graphicForm.palette?.trim(),
      motif: graphicForm.motif?.trim(),
      finish: graphicForm.finish?.trim(),
    };

    if (!Object.values(fields).some(Boolean)) {
      toast.error('Add at least one detail before building a prompt');
      return;
    }

    const parts = [];
    if (fields.style) parts.push(`style: ${fields.style}`);
    if (fields.motif) parts.push(`motif/story: ${fields.motif}`);
    if (fields.placement) parts.push(`placement: ${fields.placement}`);
    if (fields.technique) parts.push(`technique: ${fields.technique}`);
    if (fields.palette) parts.push(`palette: ${fields.palette}`);
    if (fields.finish) parts.push(`finish: ${fields.finish}`);

    const prompt = `Garment graphic concept for ${selectedLook?.categoryLabel || 'look'} — ${parts.join(' | ')}`;
    setCurrentPrompt(prompt);
    setNegativePrompt(DEFAULT_NEGATIVE_PROMPT);

    if (activeLookId) {
      updateLookPromptState(activeLookId, {
        prompt,
        negativePrompt: DEFAULT_NEGATIVE_PROMPT,
        numImages,
        qualitySteps,
        guidanceScale,
        graphicForm,
      });
    }

    toast.success('Graphic prompt ready');
  }, [activeLookId, graphicForm, guidanceScale, numImages, qualitySteps, selectedLook?.categoryLabel, updateLookPromptState]);

  const handleGenerateSku = useCallback(() => {
    if (!selectedLook) {
      toast.error('Select a look before generating a SKU');
      return;
    }

    const sequenceSegment = String(selectedLook.sequence || 1).padStart(2, '0');
    const categorySegment = formatSegment(selectedLook.categoryLabel || selectedLook.categoryKey || 'GEN', 3, 'GEN');

    const materialHint =
      dropConfig?.productInfo?.material?.split(/[\s,/|-]+/).find((token) => token && token.length >= 2) ||
      dropConfig?.productInfo?.name ||
      '';
    const variantSegment = formatSegment(materialHint, 3, 'STD');

    const sku = `${brandSkuPrefix}-${categorySegment}-${sequenceSegment}-${variantSegment}-A`;
    const defaultName =
      dropConfig?.productInfo?.name?.trim() ||
      `${selectedLook.categoryLabel || 'Product'} ${sequenceSegment}`;

    patchDropConfig((prev) => {
      if (!prev) return prev;
      const info = prev.productInfo || {};
      const schemas = info.schemas || {};
      const schemaKey = getSchemaKeyForMode(prev.mode);
      const currentSchema = schemas[schemaKey] || getDefaultSchemaForMode(prev.mode);
      const schemaAfterName = applySummaryFieldToSchema(schemaKey, currentSchema, 'name', defaultName);
      const schemaAfterSku = applySummaryFieldToSchema(schemaKey, schemaAfterName, 'sku', sku);
      return {
        ...prev,
        productInfo: {
          ...info,
          name: defaultName,
          sku,
          schemas: {
            ...schemas,
            [schemaKey]: schemaAfterSku
          }
        }
      };
    });

    toast.success('SKU generated');
  }, [brandSkuPrefix, dropConfig?.productInfo?.material, dropConfig?.productInfo?.name, patchDropConfig, selectedLook]);

  const handleProductInfoChange = useCallback(
    (field) => (event) => {
      const value = event?.target?.value ?? '';
      patchDropConfig((prev) => {
        if (!prev) return prev;
        const info = prev.productInfo || {};
        const schemas = info.schemas || {};
        const schemaKey = getSchemaKeyForMode(prev.mode);
        const currentSchema = schemas[schemaKey] || getDefaultSchemaForMode(prev.mode);
        const summaryLinkedFields = new Set([
          'name',
          'sku',
          'season',
          'collection',
          'material',
          'technicalNotes',
          'productionNotes',
          'careInstructions',
          'fit',
          'sizeRun'
        ]);
        const nextSchemas = {
          ...schemas
        };
        if (summaryLinkedFields.has(field)) {
          nextSchemas[schemaKey] = applySummaryFieldToSchema(schemaKey, currentSchema, field, value);
        }

        return {
          ...prev,
          productInfo: {
            ...info,
            [field]: value,
            schemas: nextSchemas
          }
        };
      });
    },
    [patchDropConfig]
  );

  // Handler for mode-specific cost forms
  const handleProductInfoCostsChange = useCallback(
    (newCosts) => {
      patchDropConfig((prev) => ({
        ...prev,
        productInfo: {
          ...(prev.productInfo || {}),
          costs: newCosts
        }
      }));
    },
    [patchDropConfig]
  );

  const handleProductInfoDateChange = useCallback(
    (field) => (event) => {
      const value = event?.target?.value || null;
      patchDropConfig((prev) => ({
        ...prev,
        productInfo: {
          ...(prev.productInfo || {}),
          [field]: value,
        },
      }));
    },
    [patchDropConfig]
  );

  const handleSchemaChange = useCallback(
    (updater, summaryUpdates = null, targetKey = null) => {
      patchDropConfig((prev) => {
        if (!prev) return prev;
        const info = prev.productInfo || {};
        const schemas = info.schemas || {};
        const schemaKey = targetKey || getSchemaKeyForMode(prev.mode);
        const currentSchema = schemas[schemaKey] || getDefaultSchemaForMode(prev.mode);
        const result = typeof updater === 'function' ? updater(currentSchema) : updater;
        const nextProductInfo = { ...info };
        if (!nextProductInfo.schemas) {
          nextProductInfo.schemas = {};
        }
        nextProductInfo.schemas = {
          ...schemas,
          [schemaKey]: result
        };
        if (summaryUpdates && typeof summaryUpdates === 'object') {
          Object.entries(summaryUpdates).forEach(([key, value]) => {
            if (value !== undefined) {
              nextProductInfo[key] = value;
            }
          });
        }
        return {
          ...prev,
          productInfo: nextProductInfo
        };
      });
    },
    [patchDropConfig]
  );

  const handleApplyPromptFromConfig = useCallback(() => {
    if (!activeLookId) {
      toast.error('Select a look before applying prompts.');
      return;
    }

    if (!dropConfig?.assets) {
      toast.error('No prompt configured for this look yet.');
      return;
    }

    if (dropConfig?.assets?.prompt) {
      setCurrentPrompt(dropConfig.assets.prompt);
    }
    if (dropConfig?.assets?.negativePrompt) {
      setNegativePrompt(dropConfig.assets.negativePrompt);
    }

    toast.success('Applied drop prompt to Ideogram config.');
  }, [activeLookId, dropConfig, setCurrentPrompt, setNegativePrompt]);

  const handleDropSave = useCallback(async (nextStatus = 'ready') => {
    if (!brandId || !dropConfig) {
      return;
    }

    try {
      setDropSaving(true);
      setDropError(null);

      const payload = {
        ...dropConfig,
        status: nextStatus,
      };

      const response = await saveDropConfiguration(brandId, dropConfig.lookId, payload);

      const persistedConfig = response?.config || { ...payload };
      if (!persistedConfig.status) {
        persistedConfig.status = nextStatus;
      }

      setDropConfig(persistedConfig);
      setDropBudget(response?.budget || dropBudget);
      setDropPlanningStatus(response?.planningStatus || dropPlanningStatus || planning?.status || null);
      setDropDirty(false);
      const lookId = persistedConfig?.lookId || dropConfig.lookId;
      if (lookId) {
        const entry = dropStateCacheRef.current[lookId] || {};
        dropStateCacheRef.current = {
          ...dropStateCacheRef.current,
          [lookId]: {
            ...entry,
            config: persistedConfig,
            budget: response?.budget ?? dropBudget ?? entry.budget ?? null,
            planningStatus:
              response?.planningStatus ?? dropPlanningStatus ?? planning?.status ?? entry.planningStatus ?? null,
            dirty: false,
          },
        };
      }

      setPlanning((prev) => {
        if (!prev) return prev;
        const configs = Array.isArray(prev.lookConfigurations) ? [...prev.lookConfigurations] : [];
        const idx = configs.findIndex((item) => item.lookId === (persistedConfig?.lookId || dropConfig.lookId));
        const nextConfig = persistedConfig;
        if (idx >= 0) {
          configs[idx] = nextConfig;
        } else {
          configs.push(nextConfig);
        }

        return {
          ...prev,
          lookConfigurations: configs,
        };
      });

      toast.success(nextStatus === 'ready' ? 'Drop configuration saved' : 'Draft saved');
    } catch (error) {
      console.error('Failed to save drop configuration', error);
      setDropError(error.response?.data?.message || error.message || 'Unable to save drop configuration');
      toast.error(error.response?.data?.message || error.message || 'Failed to save drop configuration');
    } finally {
      setDropSaving(false);
    }
  }, [brandId, dropConfig, dropBudget, dropPlanningStatus, planning?.status]);

  const persistAssetLibrary = useCallback(
    async (nextLibrary) => {
      if (!brandId || !dropConfig?.lookId) {
        return;
      }

      try {
        const payload = {
          ...dropConfig,
          assets: {
            ...(dropConfig.assets || {}),
            library: nextLibrary,
          },
        };

        const response = await saveDropConfiguration(brandId, dropConfig.lookId, payload);
        const persistedConfig = response?.config || payload;

        setDropConfig(persistedConfig);
        setDropBudget(response?.budget || dropBudget);
        setDropPlanningStatus(
          response?.planningStatus || dropPlanningStatus || planning?.status || null
        );
        setDropDirty(false);

        const lookId = persistedConfig?.lookId || dropConfig.lookId;
        if (lookId) {
          const entry = dropStateCacheRef.current[lookId] || {};
          dropStateCacheRef.current = {
            ...dropStateCacheRef.current,
            [lookId]: {
              ...entry,
              config: persistedConfig,
              budget: response?.budget ?? dropBudget ?? entry.budget ?? null,
              planningStatus:
                response?.planningStatus ??
                dropPlanningStatus ??
                planning?.status ??
                entry.planningStatus ??
                null,
              dirty: false,
            },
          };
        }

        setPlanning((prev) => {
          if (!prev) return prev;
          const configs = Array.isArray(prev.lookConfigurations) ? [...prev.lookConfigurations] : [];
          const idx = configs.findIndex((item) => item.lookId === (persistedConfig?.lookId || dropConfig.lookId));
          if (idx >= 0) {
            configs[idx] = persistedConfig;
          } else {
            configs.push(persistedConfig);
          }

          return {
            ...prev,
            lookConfigurations: configs,
          };
        });
      } catch (error) {
        console.error('Failed to persist assets', error);
        toast.error(error.response?.data?.message || 'Failed to save assets');
      }
    },
    [brandId, dropConfig, dropBudget, dropPlanningStatus, planning?.status]
  );

  const persistAssets = useCallback(
    async (nextAssets) => {
      if (!brandId || !dropConfig?.lookId) {
        return;
      }

      try {
        const payload = {
          ...(dropConfig || {}),
          assets: nextAssets,
        };

        const response = await saveDropConfiguration(brandId, dropConfig.lookId, payload);
        const persistedConfig = response?.config || payload;

        setDropConfig(persistedConfig);
        setDropBudget(response?.budget || dropBudget);
        setDropPlanningStatus(
          response?.planningStatus || dropPlanningStatus || planning?.status || null
        );
        setDropDirty(false);

        const lookId = persistedConfig?.lookId || dropConfig.lookId;
        if (lookId) {
          const entry = dropStateCacheRef.current[lookId] || {};
          dropStateCacheRef.current = {
            ...dropStateCacheRef.current,
            [lookId]: {
              ...entry,
              config: persistedConfig,
              budget: response?.budget ?? dropBudget ?? entry.budget ?? null,
              planningStatus:
                response?.planningStatus ??
                dropPlanningStatus ??
                planning?.status ??
                entry.planningStatus ??
                null,
              dirty: false,
            },
          };
        }

        setPlanning((prev) => {
          if (!prev) return prev;
          const configs = Array.isArray(prev.lookConfigurations) ? [...prev.lookConfigurations] : [];
          const idx = configs.findIndex((item) => item.lookId === (persistedConfig?.lookId || dropConfig.lookId));
          if (idx >= 0) {
            configs[idx] = persistedConfig;
          } else {
            configs.push(persistedConfig);
          }

          return {
            ...prev,
            lookConfigurations: configs,
          };
        });
      } catch (error) {
        console.error('Failed to persist assets', error);
        toast.error(error.response?.data?.message || 'Failed to save assets');
      }
    },
    [brandId, dropConfig, dropBudget, dropPlanningStatus, planning?.status]
  );

  const handleUploadAssets = useCallback(
    async (event) => {
      if (!activeLookId) {
        toast.error('Select a look to attach these assets first.');
        if (event?.target) {
          event.target.value = '';
        }
        return;
      }

      const files = Array.from(event.target?.files || []);
      if (files.length === 0) {
        if (event.target) {
          event.target.value = '';
        }
        return;
      }

      setAssetUploadBusy(true);

      try {
        const filePayloads = await Promise.all(
          files.map(
            (file, index) =>
              new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () =>
                  resolve({
                    url: reader.result,
                    name: file.name || `Upload ${index + 1}`,
                    size: file.size,
                    type: file.type,
                  });
                reader.onerror = reject;
                reader.readAsDataURL(file);
              })
          )
        );

        const now = Date.now();
        const newEntries = filePayloads.map((item, index) => {
          const createdAt = new Date(now + index).toISOString();
          return {
            id: `${now}-${index}-${Math.random().toString(36).slice(2, 10)}`,
            name: item.name || 'Uploaded Asset',
            sourceType: 'upload',
            url: item.url,
            createdAt,
            updatedAt: createdAt,
            category: assetCategorySelection,
            lookId: activeLookId,
            meta: {
              mimeType: item.type,
              size: item.size,
              lookId: activeLookId,
            },
          };
        });

        const nextLibrary = [...newEntries, ...assetLibrary];
        applyAssetLibrary(() => nextLibrary);

        toast.success(`${filePayloads.length} asset${filePayloads.length > 1 ? 's' : ''} added`);
        await persistAssetLibrary(nextLibrary);
      } catch (error) {
        console.error('Asset upload failed', error);
        toast.error('Failed to add assets');
      } finally {
        setAssetUploadBusy(false);
        if (event.target) {
          event.target.value = '';
        }
      }
    },
    [activeLookId, assetCategorySelection, applyAssetLibrary, assetLibrary, persistAssetLibrary]
  );

  const handleUploadReferenceImages = useCallback(
    async (event) => {
      if (!activeLookId || !dropConfig) {
        toast.error('Select a look to attach these assets first.');
        if (event?.target) {
          event.target.value = '';
        }
        return;
      }

      const files = Array.from(event.target?.files || []);
      if (files.length === 0) {
        if (event.target) {
          event.target.value = '';
        }
        return;
      }

      try {
        const fileUrls = await Promise.all(
          files.map(
            (file) =>
              new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
              })
          )
        );

        const existing = Array.isArray(dropConfig?.assets?.referenceImages)
          ? dropConfig.assets.referenceImages
          : [];
        const nextReferenceImages = [...existing, ...fileUrls];

        patchDropConfig((prev) => ({
          ...prev,
          assets: {
            ...(prev.assets || {}),
            referenceImages: nextReferenceImages,
          },
        }));

        await persistAssets({
          ...(dropConfig?.assets || {}),
          referenceImages: nextReferenceImages,
        });

        toast.success(`${fileUrls.length} reference image${fileUrls.length > 1 ? 's' : ''} added`);
      } catch (error) {
        console.error('Failed to upload reference images', error);
        toast.error('Failed to add reference images');
      } finally {
        if (event?.target) {
          event.target.value = '';
        }
      }
    },
    [activeLookId, dropConfig, patchDropConfig, persistAssets]
  );

  const handleRemoveReferenceImage = useCallback(
    async (index) => {
      const existing = Array.isArray(dropConfig?.assets?.referenceImages)
        ? dropConfig.assets.referenceImages
        : [];
      if (index < 0 || index >= existing.length) return;

      const nextReferenceImages = existing.filter((_, idx) => idx !== index);
      patchDropConfig((prev) => ({
        ...prev,
        assets: {
          ...(prev.assets || {}),
          referenceImages: nextReferenceImages,
        },
      }));

      await persistAssets({
        ...(dropConfig?.assets || {}),
        referenceImages: nextReferenceImages,
      });
    },
    [dropConfig, patchDropConfig, persistAssets]
  );

  const handleAddGeneratedImageToAssets = useCallback(
    (imageUrl) => {
      if (!activeLookId) {
        toast.error('Select a look before saving renders.');
        return;
      }
      if (!imageUrl) return;

      const exists = assetLibrary.some(
          (entry) =>
            entry.sourceType === 'ideogram' &&
            entry.url === imageUrl &&
            (entry.meta?.lookId ?? entry.lookId ?? null) === activeLookId
        );
      if (exists) {
        toast('Already saved to Assets', { icon: 'ℹ️' });
        return;
      }

      const timestamp = new Date().toISOString();
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        name: selectedLook ? `${selectedLook.categoryLabel || 'Look'} Asset` : 'Ideogram Asset',
        sourceType: 'ideogram',
        url: imageUrl,
        createdAt: timestamp,
        updatedAt: timestamp,
        category: assetCategorySelection,
        lookId: activeLookId,
        meta: {
          prompt: currentPrompt || '',
          negativePrompt: negativePrompt || '',
          lookId: activeLookId,
        },
      };

      applyAssetLibrary((prev) => [entry, ...prev]);
      toast.success('Saved render to Assets');
    },
    [activeLookId, assetCategorySelection, applyAssetLibrary, assetLibrary, currentPrompt, negativePrompt, selectedLook]
  );

  const handleRemoveAsset = useCallback(async (id) => {
    if (!id) return;

    if (!brandId || !dropConfig?.lookId) {
      applyAssetLibrary((prev) => prev.filter((item) => item.id !== id));
      toast.success('Asset removed');
      return;
    }

    try {
      const response = await deleteDropAsset(brandId, dropConfig.lookId, id);
      const serverLibrary = Array.isArray(response?.assets?.library) ? response.assets.library : [];
      const normalized = serverLibrary
        .map((item) => normalizeAssetEntry(item, dropConfig.lookId))
        .filter(Boolean);

      setAssetLibrary(normalized);
      setDropConfig((prev) => {
        if (!prev || prev.lookId !== dropConfig.lookId) {
          return prev;
        }
        const nextAssets = {
          ...(prev.assets || {}),
          library: normalized,
        };
        if (response?.config?.assets) {
          if (Array.isArray(response.config.assets.referenceImages)) {
            nextAssets.referenceImages = response.config.assets.referenceImages;
          }
          if (typeof response.config.assets.prompt === 'string') {
            nextAssets.prompt = response.config.assets.prompt;
          }
          if (typeof response.config.assets.negativePrompt === 'string') {
            nextAssets.negativePrompt = response.config.assets.negativePrompt;
          }
        }

        const nextConfig = {
          ...prev,
          assets: nextAssets,
        };

        if (response?.config?.lastUpdatedAt) {
          nextConfig.lastUpdatedAt = response.config.lastUpdatedAt;
        }

        return nextConfig;
      });
      setPlanning((prev) => {
        if (!prev) return prev;
        const configs = Array.isArray(prev.lookConfigurations) ? [...prev.lookConfigurations] : [];
        const index = configs.findIndex((item) => item.lookId === dropConfig.lookId);
        if (index === -1) {
          return prev;
        }

        const baseConfig = configs[index] || {};
        const nextConfig = {
          ...baseConfig,
          assets: {
            ...(baseConfig.assets || {}),
            library: normalized,
          },
        };

        configs[index] = nextConfig;
        return {
          ...prev,
          lookConfigurations: configs,
        };
      });
      toast.success('Asset removed');
    } catch (error) {
      console.error('Failed to delete asset', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to delete asset');
    }
  }, [applyAssetLibrary, brandId, dropConfig]);

  const handleClearAssetLibrary = useCallback(() => {
    if (!activeLookId) {
      applyAssetLibrary([]);
      toast.success('Cleared asset library');
      return;
    }

    applyAssetLibrary((prev) =>
      prev.filter((asset) => {
        const lookId = asset.meta?.lookId ?? asset.lookId ?? null;
        return lookId !== activeLookId;
      })
    );
    toast.success('Cleared assets for this look');
  }, [activeLookId, applyAssetLibrary]);

  const handleAssetCategorySelectionChange = useCallback((event) => {
    const value = event.target.value;
    if (ASSET_CATEGORIES.includes(value)) {
      setAssetCategorySelection(value);
    }
  }, []);

  const handleAssetCategoryChange = useCallback(
    (id, category) => {
      if (!ASSET_CATEGORIES.includes(category)) return;
      const timestamp = new Date().toISOString();
      applyAssetLibrary((prev) =>
        prev.map((asset) =>
          asset.id === id
            ? {
                ...asset,
                category,
                updatedAt: timestamp,
              }
            : asset
        )
      );
    },
    [applyAssetLibrary]
  );

  const handleAssetLookChange = useCallback(
    (id, lookId) => {
      const normalizedLookId = lookId || null;
      const timestamp = new Date().toISOString();
      applyAssetLibrary((prev) =>
        prev.map((asset) =>
          asset.id === id
            ? {
                ...asset,
                meta: {
                  ...(asset.meta || {}),
                  lookId: normalizedLookId,
                },
                lookId: normalizedLookId,
                updatedAt: timestamp,
              }
            : asset
        )
      );
    },
    [applyAssetLibrary]
  );

  const handleDownloadAsset = useCallback((asset) => {
    if (!asset?.url) return;
    const link = document.createElement('a');
    link.href = asset.url;
    link.download = `${asset.name || 'asset'}-${Date.now()}.png`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  }, []);

  const handleApplyAssetPrompt = useCallback(
    (asset) => {
      const prompt = asset?.meta?.prompt;
      const negative = asset?.meta?.negativePrompt;
      if (!prompt) {
        toast('No prompt stored with this asset', { icon: 'ℹ️' });
        return;
      }
      setCurrentPrompt(prompt);
      if (negative) {
        setNegativePrompt(negative);
      }
      toast.success('Prompt applied to Ideogram config');
    },
    []
  );

  const handleChecklistToggle = (id) => {
    setChecklistState((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status: item.status === 'done' ? 'in_progress' : 'done',
            }
          : item
      )
    );
  };

  useEffect(() => {
    if (dropOnlyMode) {
      setActiveNav('budgetTracker');
    }
  }, [dropOnlyMode]);

  const navigationItems = useMemo(() => {
    if (dropOnlyMode) {
      return [{ key: 'budgetTracker', label: 'Budget Tracker' }];
    }
    return [
      { key: 'collectionPlan', label: '💎 Collection Plan' },
      { key: 'productInfo', label: 'Product Info' },
      { key: 'graphic', label: 'Graphic Design' },
      { key: 'mockup', label: '🎨 Mockup Builder' },
      { key: 'marketing', label: 'Marketing Visuals' },
      { key: 'assets', label: 'Assets' },
      { key: 'lookbook', label: 'Lookbook', disabled: true },
      { key: 'archive', label: 'Archive', disabled: true },
    ];
  }, [dropOnlyMode]);


  const handlePromptGenerated = (promptData) => {
    // Use currentPrompt from the callback if provided, otherwise use ideogram_prompt or narrative
    const promptText = promptData.currentPrompt || promptData.ideogram_prompt || promptData.narrative ||
      (typeof promptData.prompt === 'string' ? promptData.prompt : JSON.stringify(promptData.prompt));

    setCurrentPrompt(promptText);

    // Set negative prompt from callback or fallback
    if (promptData.negativePrompt) {
      setNegativePrompt(promptData.negativePrompt);
    } else if (promptData.negative_prompt) {
      setNegativePrompt(promptData.negative_prompt);
    }

    // Update Ideogram config if provided
    if (promptData.ideogramConfig) {
      setNumImages(promptData.ideogramConfig.numImages);
      setQualitySteps(promptData.ideogramConfig.qualitySteps);
      setGuidanceScale(promptData.ideogramConfig.guidanceScale);
    }

    if (activeLookId) {
      updateLookPromptState(activeLookId, {
        prompt: promptText,
        negativePrompt:
          promptData.negativePrompt || promptData.negative_prompt || DEFAULT_NEGATIVE_PROMPT,
        numImages: promptData.ideogramConfig?.numImages ?? numImages,
        qualitySteps: promptData.ideogramConfig?.qualitySteps ?? qualitySteps,
        guidanceScale: promptData.ideogramConfig?.guidanceScale ?? guidanceScale,
        graphicForm,
      });
    }

    toast.success('Prompt configured! Click "Generate" to create images.');
  };

  const handleGenerate = async () => {
    if (!currentPrompt || !currentPrompt.trim()) {
      toast.error('Please add a prompt in the "Ideogram Config" tab below');
      return;
    }

    if (aiServerStatus !== 'online') {
      toast.error('Replicate API not configured. Please add REPLICATE_API_TOKEN to .env file.');
      return;
    }

    if (!activeLookId) {
      toast.error('Select a look before generating new designs.');
      return;
    }

    try {
      setLoading(true);
      setGeneratedImages([]);
      if (activeLookId) {
        setLookGeneratedImages((prev) => ({
          ...prev,
          [activeLookId]: [],
        }));
      }

      const response = await fetch('/api/design-studio/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: currentPrompt,
          negative_prompt: negativePrompt,
          num_images: numImages,
          num_inference_steps: qualitySteps,
          guidance_scale: guidanceScale,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Generation failed');
      }

      setGeneratedImages(data.images || []);
      if (activeLookId) {
        setLookGeneratedImages((prev) => ({
          ...prev,
          [activeLookId]: data.images || [],
        }));
      }
      toast.success(`Generated ${data.images.length} design(s) successfully!`);
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.message || 'Failed to generate designs');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDesign = async () => {
    if (!selectedImage || !designName.trim()) {
      toast.error('Please enter a design name');
      return;
    }

    try {
      const response = await fetch('/api/design-studio/save-design', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brandId,
          imageUrl: selectedImage,
          prompt: currentPrompt,
          name: designName,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Save failed');
      }

      toast.success('Design saved successfully!');
      setSaveDialogOpen(false);
      setDesignName('');
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save design');
    }
  };

  const handleDownload = (imageUrl, index) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `design-${Date.now()}-${index}.png`;
    link.target = '_blank';
    link.click();
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', position: 'relative' }}>
      {/* Left Sidebar - Config Controls */}
      <Paper
        elevation={3}
        sx={{
          width: 280,
          flexShrink: 0,
          p: 1.5,
          overflowY: 'auto',
          borderRadius: 2,
          height: '100%',
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
          {dropOnlyMode ? 'Drop Planning Controls' : 'Design Studio Controls'}
        </Typography>

        {!dropOnlyMode && (
          <>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.key}
                  variant={activeNav === item.key ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => !item.disabled && handleNavClick(item.key)}
                  disabled={item.disabled}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    opacity: item.disabled ? 0.6 : 1
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>

            <Button
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              onClick={() => navigate(`/brands/${brandId}/planning/drop`)}
            >
              Open Drop Planning
            </Button>
          </>
        )}

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mb: 2 }}>
          {planningLoading ? (
            <Box sx={{ py: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary">
                Loading guardrails...
              </Typography>
            </Box>
          ) : planningError ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {planningError}
            </Alert>
          ) : planning ? (
            <Box
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.light',
                p: 1.5,
                mb: 2,
                bgcolor: 'primary.50',
              }}
            >
              <Typography variant="caption" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
                BRAND GUARDRAILS
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <Stack spacing={1}>
                  <Accordion defaultExpanded={false} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2" fontWeight={600}>
                        Financial Targets
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip size="small" label={`Revenue: ${formatCurrency(revenueGoal)}`} />
                          <Chip size="small" label={`MU: ${formatPercent(marginTarget)}`} />
                          <Chip
                            size="small"
                            label={`DTC ${formatPercent(planning.financialPlan?.channelSplit?.directToConsumer || 0)} / Wholesale ${formatPercent(planning.financialPlan?.channelSplit?.wholesale || 0)}`}
                          />
                          <Chip size="small" label={`COGS Budget: ${formatCurrency(totalCogsBudget)}`} />
                          <Chip size="small" label={`Gross Margin: ${formatCurrency(totalGrossMargin)}`} />
                          <Chip size="small" label={`Per Look COGS: ${formatCurrency(perLookCogsBudget)}`} />
                          <Chip size="small" label={`Per Unit COGS: ${formatCurrency(perUnitCogsBudget)}`} />
                          <Chip size="small" label={`Per Look Margin: ${formatCurrency(perLookGrossMargin)}`} />
                          <Chip size="small" label={`Per Unit Margin: ${formatCurrency(perUnitGrossMargin)}`} />
                          <Chip size="small" label={`Discount: ${formatPercent(planning.financialPlan?.plannedDiscountRate || 0)}`} />
                          <Chip size="small" label={`Cost Split: ${formatPercent(materialsShare)} materials / ${formatPercent(laborShare)} labor`} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          Materials Budget: {formatCurrency(totalCogsBudget * materialsShare)} • Labor Budget: {formatCurrency(totalCogsBudget * laborShare)}
                        </Typography>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {productMixTargets.length > 0 && (
                    <Accordion defaultExpanded={false} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2" fontWeight={600}>
                          Hero Categories
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={0.5}>
                          {productMixTargets
                            .slice()
                            .sort((a, b) => (b.share || 0) - (a.share || 0))
                            .slice(0, 4)
                            .map((category) => (
                              <Typography key={category.categoryId || category.categoryLabel} variant="caption" color="text.secondary">
                                • {category.categoryLabel || 'Category'} — {formatPercent(category.share)}
                                {category.plannedLooks ? ` (${category.plannedLooks} looks)` : ''}
                                {category.plannedUnits ? ` • ${category.plannedUnits.toLocaleString()} units` : ''}
                                {` • Retail ${formatCurrency(category.perLookRetail || 0)} • COGS ${formatCurrency(category.perLookCogs || 0)} (${formatCurrency(category.materialsBudgetPerLook || 0)} materials / ${formatCurrency(category.laborBudgetPerLook || 0)} labor)`}
                              </Typography>
                            ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  <Accordion defaultExpanded={false} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2" fontWeight={600}>
                        Design Notes
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {planning.assumptions?.trim() || 'Capture aesthetic direction, campaign tone, and detail cues in the planner.'}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </Stack>
            </Box>
          ) : null}
        </Box>
      </Paper>

      {/* Main Canvas Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', ml: 3, overflowY: 'auto', minHeight: 0 }}>
        {/* Kam Hero Header - appears on all Design Studio pages */}
        <KamHeroHeader
          brandId={brandId}
          bimesterOptions={bimesterOptions}
          selectedBimester={selectedBimesterId || dropConfig?.dropCycle || activeDropCycle?.cycleId || ''}
          onBimesterChange={handleBimesterChange}
          lookOptions={(() => {
            if (committedLooks.length > 0) {
              return committedLooks.map((look) => ({
                value: look.id,
                label: look.productInfo?.name || look.tabLabel || look.categoryLabel || `Look ${look.sequence}`
              }));
            }
            return [];
          })()}
          selectedLook={activeLookId || ''}
          onLookChange={(e) => {
            const lookId = e.target.value;
            const lookIndex = committedLooks.findIndex(l => l.id === lookId);
            if (lookIndex !== -1) {
              setActiveLookIndex(lookIndex);
            }
          }}
          category={selectedLook?.categoryLabel || bimesterProductMix?.[0]?.categoryLabel || planning?.productMix?.[0]?.categoryLabel || 'Outerwear'}
          bimesterRevenue={formatCurrency(revenueGoal)}
          cogsBudget={formatCurrency(totalCogsBudget)}
          perUnitCogs={formatCurrency(perUnitCogsBudget)}
          perUnitRetail={formatCurrency(perUnitCogsBudget / Math.max(0.01, 1 - marginTarget))}
          perLookCogs={formatCurrency(perLookCogsBudget)}
          perLookRetail={formatCurrency(perLookCogsBudget / Math.max(0.01, 1 - marginTarget))}
          plannedLooks={String(plannedLooksTarget)}
          plannedUnits={String(effectiveUnits)}
          subtitle={`Capture product naming, SKU structure, material details, and technical notes for ${activeLookName || selectedLook?.categoryLabel || 'your collection'}.`}
        />

        {/* Reset Looks Toolbar */}
        {committedLooks.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => handleResetLooks(false)}
            >
              Reset This Bimester's Looks
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleResetLooks(true)}
            >
              Reset All Looks
            </Button>
          </Box>
        )}

        {activeNav === 'budgetTracker' && committedLooks.length === 0 && (
          <Card
            variant="outlined"
            sx={{
              mb: 2,
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Drop Configuration
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Commit a merchandising plan with planned looks to unlock per-look drop configuration. Once the planner is committed, each look will appear as a tab above the canvas so you can assign sourcing, costs, and design prompts.
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Tip: Planned looks are derived from the product mix card in the planner. Each look becomes a slot in the design studio once your plan is committed.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {activeNav === 'budgetTracker' && committedLooks.length > 0 && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            {dropError && (
              <Alert severity="error">
                {dropError}
              </Alert>
            )}
            {catalogError && (
              <Alert severity="warning">
                Supplier and pattern catalogs are partially unavailable. Existing configurations remain editable, but lookups may be limited. ({catalogError})
              </Alert>
            )}
            {dropPlanningStatus && dropPlanningStatus !== 'committed' && (
              <Alert severity="info">
                Planner status: <strong>{dropPlanningStatus}</strong>. You can prepare sourcing now, but guardrails may update once the plan is committed.
              </Alert>
            )}

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    rowGap={1}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      Budget Guardrails
                    </Typography>
                    {Number.isFinite(dropBudget?.shareAllocation) && (
                      <Chip
                        label={`Category Share ${formatPercent(dropBudget.shareAllocation)}`}
                        size="small"
                      />
                    )}
                  </Stack>

                  {dropLoading && <LinearProgress />}

                  {dropBudgetChips.length > 0 && (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap">
                      {dropBudgetChips.map((label) => (
                        <Chip key={label} label={label} size="small" />
                      ))}
                    </Stack>
                  )}

                  {categoryUnitsGuardrail && categoryUnitsGuardrail.plannedUnits > 0 && (
                    <Stack spacing={0.25}>
                      <Typography variant="caption" color="text.secondary">
                        Category Units Plan: {formatUnits(categoryUnitsGuardrail.plannedUnits)} • Looks in category:{' '}
                        {formatUnits(categoryUnitsGuardrail.totalLooksForCategory)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={
                          categoryUnitsGuardrail.totalAssigned > categoryUnitsGuardrail.plannedUnits
                            ? 'error.main'
                            : 'text.secondary'
                        }
                      >
                        Accounted units: {formatUnits(categoryUnitsGuardrail.totalAssigned)} /{' '}
                        {formatUnits(categoryUnitsGuardrail.plannedUnits)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Locked allocation: {formatUnits(categoryUnitsGuardrail.lockedUnits)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={
                          categoryUnitsGuardrail.remainingForOthers > 0 ? 'text.secondary' : 'warning.main'
                        }
                      >
                        Remaining units for other looks: {formatUnits(categoryUnitsGuardrail.remainingForOthers)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Baseline per look: {formatUnits(Math.round(categoryUnitsGuardrail.baselinePerLook))}
                      </Typography>
                    </Stack>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Per Unit Guidance
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(dropBudget?.perUnitCogs || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Retail {formatCurrency(dropBudget?.perUnitRetail || dropBudget?.perUnitRevenue || 0)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Per Look Guidance
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(dropBudget?.perLookCogs || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Retail {formatCurrency(dropBudget?.perLookRetail || 0)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Estimated Spend
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(dropEstimates.totalCost)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={dropEstimates.withinBudget ? 'success.main' : 'error.main'}
                      >
                        {dropEstimates.withinBudget
                          ? `Within by ${formatCurrency(dropEstimates.remaining)}`
                          : `Over by ${formatCurrency(Math.abs(dropEstimates.remaining))}`}
                      </Typography>
                    </Box>
                  </Stack>

                  <Alert severity={dropEstimates.withinBudget ? 'success' : 'warning'}>
                    {dropEstimates.withinBudget
                      ? `Estimated production spend of ${formatCurrency(dropEstimates.totalCost)} stays within the guardrail of ${formatCurrency(dropEstimates.budget)}.`
                      : `Estimated production spend of ${formatCurrency(dropEstimates.totalCost)} exceeds the guardrail of ${formatCurrency(dropEstimates.budget)} by ${formatCurrency(Math.abs(dropEstimates.remaining))}. Adjust units or per-unit costs to stay aligned.`}
                  </Alert>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Budget & Cost Tracker — {activeLookName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Monitor budget allocation, cost structure, and financial performance for this look.
                    </Typography>
                  </Box>

                  {dropConfig ? (
                    <>
                      {/* Budget Allocation Overview */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Budget Allocation
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                              <Typography variant="caption" color="text.secondary">
                                Per Look COGS Budget
                              </Typography>
                              <Typography variant="h6" fontWeight={600} color="primary.main">
                                {formatCurrency(perLookCogsBudget)}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                              <Typography variant="caption" color="text.secondary">
                                Per Unit COGS Budget
                              </Typography>
                              <Typography variant="h6" fontWeight={600} color="primary.main">
                                {formatCurrency(perUnitCogsBudget)}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                              <Typography variant="caption" color="text.secondary">
                                Units Planned
                              </Typography>
                              <Typography variant="h6" fontWeight={600}>
                                {dropConfig.units || 0}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                              <Typography variant="caption" color="text.secondary">
                                Total Look Budget
                              </Typography>
                              <Typography variant="h6" fontWeight={600}>
                                {formatCurrency((dropConfig.units || 0) * perUnitCogsBudget)}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Cost Structure Breakdown */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Cost Structure
                        </Typography>
                        <Stack spacing={2}>
                          {/* Materials */}
                          <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography variant="body2">
                                Materials ({Math.round(materialsShare * 100)}%)
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {formatCurrency(perUnitCogsBudget * materialsShare)}
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={materialsShare * 100}
                              sx={{ height: 8, borderRadius: 1 }}
                            />
                          </Box>

                          {/* Labor */}
                          <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography variant="body2">
                                Labor ({Math.round(laborShare * 100)}%)
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {formatCurrency(perUnitCogsBudget * laborShare)}
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={laborShare * 100}
                              sx={{ height: 8, borderRadius: 1 }}
                              color="secondary"
                            />
                          </Box>
                        </Stack>
                      </Box>

                      {/* Actual Costs vs Budget */}
                      {(dropConfig.wholesale?.unitCost ||
                        dropConfig.cutAndSew?.fabricCostPerUnit ||
                        dropConfig.productInfo?.costs?.materials) && (
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Actual vs Budget
                          </Typography>
                          <Stack spacing={1.5}>
                            {dropConfig.wholesale?.unitCost && (
                              <Box>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                  <Typography variant="body2">Wholesale Unit Cost</Typography>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" fontWeight={600}>
                                      {formatCurrency(dropConfig.wholesale.unitCost)}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={
                                        dropConfig.wholesale.unitCost <= perUnitCogsBudget
                                          ? 'Within Budget'
                                          : 'Over Budget'
                                      }
                                      color={dropConfig.wholesale.unitCost <= perUnitCogsBudget ? 'success' : 'warning'}
                                    />
                                  </Stack>
                                </Stack>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min((dropConfig.wholesale.unitCost / perUnitCogsBudget) * 100, 100)}
                                  sx={{ height: 6, borderRadius: 1 }}
                                  color={dropConfig.wholesale.unitCost <= perUnitCogsBudget ? 'success' : 'warning'}
                                />
                              </Box>
                            )}

                            {dropConfig.cutAndSew && (
                              (() => {
                                const totalCutAndSewCost =
                                  (dropConfig.cutAndSew.fabricCostPerUnit || 0) +
                                  (dropConfig.cutAndSew.laborCostPerUnit || 0) +
                                  (dropConfig.cutAndSew.trimsCostPerUnit || 0);
                                return totalCutAndSewCost > 0 ? (
                                  <Box>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                      <Typography variant="body2">Cut & Sew Total Cost</Typography>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="body2" fontWeight={600}>
                                          {formatCurrency(totalCutAndSewCost)}
                                        </Typography>
                                        <Chip
                                          size="small"
                                          label={
                                            totalCutAndSewCost <= perUnitCogsBudget
                                              ? 'Within Budget'
                                              : 'Over Budget'
                                          }
                                          color={totalCutAndSewCost <= perUnitCogsBudget ? 'success' : 'warning'}
                                        />
                                      </Stack>
                                    </Stack>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.min((totalCutAndSewCost / perUnitCogsBudget) * 100, 100)}
                                      sx={{ height: 6, borderRadius: 1 }}
                                      color={totalCutAndSewCost <= perUnitCogsBudget ? 'success' : 'warning'}
                                    />
                                  </Box>
                                ) : null;
                              })()
                            )}
                          </Stack>
                        </Box>
                      )}

                      {/* Financial Metrics */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Financial Metrics
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Target Margin
                              </Typography>
                              <Typography variant="h6" fontWeight={600} color="success.main">
                                {Math.round(marginTarget * 100)}%
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Per Unit Margin
                              </Typography>
                              <Typography variant="h6" fontWeight={600}>
                                {formatCurrency(perUnitGrossMargin)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Per Look Margin
                              </Typography>
                              <Typography variant="h6" fontWeight={600}>
                                {formatCurrency(perLookGrossMargin)}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Budget Override Notice */}
                      {dropConfig.budgetOverrides?.totalCogs && (
                        <Alert severity="info" variant="outlined">
                          <Typography variant="body2" fontWeight={600}>
                            Budget Override Active
                          </Typography>
                          <Typography variant="caption">
                            Total COGS Budget Override: {formatCurrency(dropConfig.budgetOverrides.totalCogs)}
                          </Typography>
                        </Alert>
                      )}
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Select a look to view budget tracking information
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}

        {activeNav === 'assets' && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Asset Library
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Upload reference images or capture Ideogram renders for quick access. Items stay in your browser until you clear them.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                    <TextField
                      select
                      label="New asset category"
                      value={assetCategorySelection}
                      onChange={handleAssetCategorySelectionChange}
                      size="small"
                      sx={{ minWidth: 200 }}
                      helperText="Applied to uploads & saved renders"
                    >
                      {ASSET_CATEGORIES.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      variant="contained"
                      size="small"
                      component="label"
                      startIcon={<UploadIcon />}
                      disabled={assetUploadBusy || !activeLookId}
                    >
                      {assetUploadBusy ? 'Uploading…' : 'Upload Assets'}
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        multiple
                        onChange={handleUploadAssets}
                      />
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CollectionsIcon />}
                      onClick={() => setActiveNav('marketing')}
                      disabled={generatedImages.length === 0 || !activeLookId}
                    >
                      Save from Generator
                    </Button>
                    {activeLookId && filteredAssetLibrary.length > 0 && (
                      <Button
                        variant="text"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleClearAssetLibrary}
                      >
                        Clear Look Assets
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {!activeLookId ? (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Select a committed look to manage its assets.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use the look tabs above to choose a look, then upload or save new assets here.
                  </Typography>
                </CardContent>
              </Card>
            ) : filteredAssetLibrary.length === 0 ? (
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      No assets saved for this look yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload inspiration files or switch to the Content tab to generate Ideogram renders and add them here.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(auto-fill, minmax(220px, 1fr))',
                    md: 'repeat(auto-fill, minmax(260px, 1fr))',
                  },
                  gap: 2,
                }}
              >
                {filteredAssetLibrary.map((asset) => {
                  const categoryValue = ASSET_CATEGORIES.includes(asset?.category)
                    ? asset.category
                    : ASSET_CATEGORIES[0];

                  return (
                    <Card key={asset.id} variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
                      <CardMedia
                        component="img"
                        image={asset.url}
                        alt={asset.name}
                        sx={{ height: 180, objectFit: 'cover', bgcolor: 'grey.100' }}
                      />
                      <CardContent sx={{ flexGrow: 1, pb: 1.5 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle2" noWrap title={asset.name}>
                            {asset.name || (asset.sourceType === 'ideogram' ? 'Ideogram Render' : 'Uploaded Asset')}
                          </Typography>
                          <TextField
                            select
                            label="Look"
                            value={asset.meta?.lookId ?? asset.lookId ?? ''}
                            onChange={(event) => handleAssetLookChange(asset.id, event.target.value || null)}
                            size="small"
                            sx={{ maxWidth: 220 }}
                            helperText="Attach to look"
                          >
                            <MenuItem value="">
                              Unassigned (Global)
                            </MenuItem>
                            {committedLooks.map((look) => (
                              <MenuItem key={look.id} value={look.id}>
                                {look.tabLabel}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            label="Category"
                            value={categoryValue}
                            onChange={(event) => handleAssetCategoryChange(asset.id, event.target.value)}
                            size="small"
                            sx={{ maxWidth: 220 }}
                            helperText="Update classification"
                          >
                            {ASSET_CATEGORIES.map((category) => (
                              <MenuItem key={category} value={category}>
                                {category}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Typography variant="caption" color="text.secondary">
                            {asset.sourceType === 'ideogram' ? 'Saved from Ideogram' : 'Uploaded'}
                            {asset.createdAt ? ` • ${new Date(asset.createdAt).toLocaleDateString()}` : ''}
                          </Typography>
                          {asset.meta?.prompt && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              Prompt: {asset.meta.prompt}
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'space-between', px: 1.5, pt: 0 }}>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadAsset(asset)}
                          >
                            Download
                          </Button>
                          <Button
                            size="small"
                            startIcon={<AIIcon />}
                            disabled={!asset.meta?.prompt}
                            onClick={() => handleApplyAssetPrompt(asset)}
                          >
                            Apply Prompt
                          </Button>
                        </Stack>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveAsset(asset.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </CardActions>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Stack>
        )}

        {activeNav === 'mockup' && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            {!activeLookId ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                Select a committed look from the tabs above to start designing.
              </Alert>
            ) : (
              <>
                <Typography variant="h5" gutterBottom>
                  🎨 Garment Design Tool
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Design print placements on your garment with our Tapstitch-style designer.
                  Upload graphics, position designs, and see real-time pricing.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    const activeLook = committedLooks.find((look) =>
                      look.id === activeLookId || look.lookId === activeLookId
                    );
                    if (!activeLook) {
                      toast.error('Please select a valid look');
                      return;
                    }

                    // Navigate to Garment Designer with look data
                    navigate('/design/garment-designer', {
                      state: {
                        product: {
                          id: activeLook.lookId,
                          name: activeLook.productInfo?.name || `${activeLook.categoryLabel} #${activeLook.sequence}`,
                          categoryLabel: activeLook.categoryLabel,
                          basePrice: activeLook.estimatedCost || 0,
                          garmentColor: 'black', // TODO: get from product
                          technique: 'DTF',
                          printAreas: [
                            {
                              name: 'Front',
                              x: 100,
                              y: 150,
                              width: 400,
                              height: 400,
                              printCost: 2.00
                            },
                            {
                              name: 'Back',
                              x: 100,
                              y: 150,
                              width: 400,
                              height: 500,
                              printCost: 2.00
                            }
                          ],
                          mockupImages: {
                            front: null,
                            back: null
                          }
                        },
                        dropConfig: {
                          productInfo: {
                            name: activeLook.productInfo?.name || '',
                            sku: activeLook.productInfo?.sku || ''
                          }
                        },
                        brandId: brandId,
                        lookId: activeLookId,
                        bimesterId: selectedBimesterId,
                        returnPath: `/brands/${brandId}/design/studio`,
                        returnState: {
                          activeNav: 'mockup',
                          activeLookId: activeLookId,
                          brandId: brandId,
                          selectedBimesterId: selectedBimesterId
                        }
                      }
                    });
                  }}
                >
                  Open Garment Designer
                </Button>
              </>
            )}
          </Box>
        )}

        {(activeNav === 'marketing' || activeNav === 'graphic') && (
          (() => {
            const isGraphic = activeNav === 'graphic';
            const emptyTitle = `Select a committed look to start ${isGraphic ? 'designing graphics' : 'building marketing visuals'}.`;
            const emptyCopy = isGraphic
              ? 'Use the look tabs above to pick a look. Graphics and prompts are stored per look so you can iterate with confidence.'
              : 'Use the look tabs above to pick a look. Prompts and renders are stored per look to keep campaign assets organized.';
            const promptEmptyCopy = isGraphic
              ? 'Use the prompt builder to craft motifs, placements, and print concepts, then click "Generate".'
              : 'Configure your prompt in the panel below, then click "Generate" to explore marketing imagery.';
            const generatorTitle = isGraphic ? 'Graphic Prompt Builder' : 'Marketing Prompt Generator';
            const referenceImages = Array.isArray(dropConfig?.assets?.referenceImages)
              ? dropConfig.assets.referenceImages
              : [];

            if (!activeLookId || !dropConfig) {
              return (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {emptyTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {emptyCopy}
                    </Typography>
                  </CardContent>
                </Card>
              );
            }

            const content = (
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  gap: 2,
                  flexGrow: 1,
                  minHeight: 0,
                }}
              >
                <Box
                  component="div"
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  }}
                >
                  {loading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                      }}
                    >
                      <CircularProgress size={60} sx={{ mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        Generating your designs...
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This typically takes 5-10 seconds
                      </Typography>
                    </Box>
                  ) : generatedImages.length === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                      }}
                    >
                      <AIIcon sx={{ fontSize: 120, color: 'text.disabled', mb: 3 }} />
                      <Typography variant="h5" color="text.secondary" gutterBottom>
                        No designs yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {promptEmptyCopy}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{ height: '100%', display: 'flex', flexWrap: 'wrap', gap: 2, alignContent: 'flex-start' }}
                    >
                      {generatedImages.map((image, index) => {
                        const alreadyInAssets = filteredAssetLibrary.some((entry) => {
                          const lookId = entry.meta?.lookId ?? entry.lookId ?? null;
                          return (
                            lookId === activeLookId &&
                            entry.sourceType === 'ideogram' &&
                            entry.url === image
                          );
                        });

                        return (
                          <Card
                            key={index}
                            elevation={4}
                            sx={{
                              width: numImages === 1 ? '100%' : 'calc(50% - 8px)',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={image}
                              alt={`Generated design ${index + 1}`}
                              sx={{
                                height: numImages === 1 ? 'calc(100vh - 350px)' : 'calc(50vh - 150px)',
                                objectFit: 'contain',
                                bgcolor: 'grey.50',
                              }}
                            />
                            <CardContent sx={{ p: 1.5, pb: 0 }}>
                              <Typography variant="caption" color="text.secondary">
                                Design {index + 1}
                              </Typography>
                            </CardContent>
                            <CardActions sx={{ p: 1.5, pt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                              <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload(image, index)}
                              >
                                Download
                              </Button>
                              <Button
                                size="small"
                                startIcon={<SaveIcon />}
                                onClick={() => {
                                  setSelectedImage(image);
                                  setSaveDialogOpen(true);
                                }}
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                startIcon={<CollectionsIcon />}
                                disabled={alreadyInAssets}
                                onClick={() => handleAddGeneratedImageToAssets(image)}
                              >
                                {alreadyInAssets ? 'In Assets' : 'Save to Assets'}
                              </Button>
                            </CardActions>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                </Box>

                <Paper
                  component="div"
                  elevation={6}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    width: { xs: '100%', lg: '40%' },
                    maxWidth: { lg: 520 },
                    minWidth: { lg: 360 },
                    alignSelf: 'flex-start',
                    position: 'sticky',
                    top: chatPanelTop + 16,
                    height: `calc(100vh - ${(chatPanelTop || 0) + 32}px)`,
                    maxHeight: `calc(100vh - ${(chatPanelTop || 0) + 32}px)`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1,
                      bgcolor: 'primary.main',
                      color: 'white',
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AIIcon sx={{ fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        {generatorTitle}
                      </Typography>
                    </Box>
                  </Box>

                  {isGraphic && (
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Quick presets
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                        {GRAPHIC_PROMPT_PRESETS.map((preset) => (
                          <Chip
                            key={preset}
                            label={preset.split(':')[0]}
                            onClick={() => handleGraphicPreset(preset)}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Reference Images
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        component="label"
                        startIcon={<UploadIcon />}
                      >
                        Upload
                        <input
                          hidden
                          accept="image/*"
                          type="file"
                          multiple
                          onChange={handleUploadReferenceImages}
                        />
                      </Button>
                    </Stack>
                    {referenceImages.length > 0 ? (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
                          gap: 1,
                        }}
                      >
                        {referenceImages.map((url, idx) => (
                          <Box
                            key={`${url}-${idx}`}
                            sx={{
                              position: 'relative',
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                            }}
                          >
                            <Box
                              component="img"
                              src={url}
                              alt={`Reference ${idx + 1}`}
                              sx={{ width: '100%', height: 80, objectFit: 'cover' }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveReferenceImage(idx)}
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                bgcolor: 'rgba(255,255,255,0.8)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Upload reference images to guide the prompt.
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      flexGrow: 1,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                    }}
                  >
                    <PromptGeneratorPro
                      brandId={brandId}
                      onPromptGenerated={handlePromptGenerated}
                      embedded={true}
                      onIdeogramGenerate={handleGenerate}
                      ideogramGenerating={loading}
                      canIdeogramGenerate={aiServerStatus === 'online' && !!currentPrompt && !!activeLookId}
                      currentPromptValue={currentPrompt}
                      onPromptChange={setCurrentPrompt}
                      negativePromptValue={negativePrompt}
                      onNegativePromptChange={setNegativePrompt}
                    />
                  </Box>
                </Paper>
              </Box>
            );

            if (isGraphic) {
              return (
                <Stack spacing={2} sx={{ mb: 2 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={2}>
                        <Typography variant="h6" fontWeight={600}>
                          Graphic Prompt Worksheet
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            select
                            label="Placement"
                            value={graphicForm.placement}
                            onChange={handleGraphicFieldChange('placement')}
                            size="small"
                            fullWidth
                          >
                            <MenuItem value="">
                              Custom placement
                            </MenuItem>
                            {GRAPHIC_PLACEMENTS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            label="Technique / Ink"
                            value={graphicForm.technique}
                            onChange={handleGraphicFieldChange('technique')}
                            size="small"
                            fullWidth
                          >
                            <MenuItem value="">
                              Custom technique
                            </MenuItem>
                            {GRAPHIC_TECHNIQUES.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            label="Art Style"
                            value={graphicForm.style}
                            onChange={handleGraphicFieldChange('style')}
                            size="small"
                            fullWidth
                          >
                            <MenuItem value="">
                              Custom style
                            </MenuItem>
                            {GRAPHIC_STYLES.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            select
                            label="Palette"
                            value={graphicForm.palette}
                            onChange={handleGraphicFieldChange('palette')}
                            size="small"
                            fullWidth
                          >
                            <MenuItem value="">
                              Custom palette
                            </MenuItem>
                            {GRAPHIC_PALETTES.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            label="Finish / Special Effect"
                            value={graphicForm.finish}
                            onChange={handleGraphicFieldChange('finish')}
                            size="small"
                            fullWidth
                          >
                            <MenuItem value="">
                              Custom finish
                            </MenuItem>
                            {GRAPHIC_FINISHES.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                        <TextField
                          label="Motif / Story"
                          value={graphicForm.motif}
                          onChange={handleGraphicFieldChange('motif')}
                          multiline
                          minRows={2}
                          size="small"
                          placeholder="Describe the narrative, iconography, or typography you want on the garment."
                        />
                      </Stack>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', px: 2, pb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={handleGraphicSuggest}
                      >
                        Suggest Fill
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={handleGraphicPromptBuild}
                      >
                        Build Prompt
                      </Button>
                    </CardActions>
                  </Card>
                  {content}
                </Stack>
              );
            }

            return content;
          })()
        )}

        {/* Collection Plan Workspace */}
        {activeNav === 'collectionPlan' && (
          <CollectionPlanWorkspace
            brandId={brandId}
            cycleLabel={planning?.currentCycle?.label || dropConfig?.cycleLabel || 'No Cycle'}
            selectedBimester={dropConfig?.dropCycle || activeDropCycle?.cycleId || ''}
            bimesterLabel={activeDropCycle?.label || 'No Cycle'}
            committedLooks={committedLooks}
          />
        )}

        {activeNav === 'productInfo' && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            {!activeLookId ? (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Select a committed look to manage product information.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Product naming, SKU structure, and material notes are captured per look so production hand-offs stay clean.
                  </Typography>
                </CardContent>
              </Card>
            ) : dropLoading && !dropConfig ? (
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <LinearProgress />
                    <Typography variant="body2" color="text.secondary">
                      Loading drop configuration…
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ) : dropConfig ? (
              <>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'flex-start', md: 'flex-start' }}
                        justifyContent="space-between"
                      >
                        <Stack spacing={1.5} direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}>
                          <Typography variant="h6" fontWeight={600}>
                            Product Information
                          </Typography>
                          <Chip
                            size="small"
                            color={dropConfig.status === 'ready' ? 'success' : 'default'}
                            label={dropConfig.status === 'ready' ? 'Ready' : 'Draft'}
                          />
                        </Stack>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Product Gallery
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                            {(() => {
                              const library = dropConfig?.assets?.library || [];
                              const productImages = library.filter((asset) => asset?.url);
                              return productImages.length > 0 ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    {productImages.length} {productImages.length === 1 ? 'image' : 'images'}
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setActiveNav('assets')}
                                  >
                                    Manage in Assets
                                  </Button>
                                </Stack>
                              ) : null;
                            })()}
                            <Button
                              variant="outlined"
                              size="small"
                              component="label"
                              startIcon={<UploadIcon />}
                              disabled={assetUploadBusy || !activeLookId}
                            >
                              {assetUploadBusy ? 'Uploading…' : 'Upload images'}
                              <input
                                hidden
                                accept="image/*"
                                type="file"
                                multiple
                                onChange={handleUploadAssets}
                              />
                            </Button>
                          </Stack>
                        </Stack>
                        <Box
                          sx={{
                            flex: 1,
                            height: 200,
                            borderRadius: 2,
                            border: '1px dashed',
                            borderColor: 'divider',
                            p: 2,
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                            width: '100%',
                            maxWidth: 'none',
                            mx: 'auto',
                            overflow: 'hidden'
                          }}
                        >
                          {(() => {
                            const library = dropConfig?.assets?.library || [];
                            const productImages = library.filter((asset) => asset?.url);

                            if (productImages.length === 0) {
                              return (
                                <Stack spacing={1} alignItems="center" textAlign="center" sx={{ width: '100%' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Add images in the Assets tab to preview them here.
                                  </Typography>
                                  <Button variant="outlined" size="small" onClick={() => setActiveNav('assets')}>
                                    Go to Assets
                                  </Button>
                                </Stack>
                              );
                            }

                            return (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                      xs: 'repeat(auto-fit, minmax(160px, 1fr))',
                                      md: 'repeat(auto-fit, minmax(180px, 1fr))'
                                    },
                                    gap: 0.75,
                                    width: '95%',
                                    height: '100%',
                                    justifyItems: 'center',
                                    alignItems: 'center',
                                    px: 0.5,
                                    margin: '0 auto',
                                    maxWidth: '100%'
                                  }}
                                >
                                  {productImages.map((image, idx) => (
                                    <Box
                                      key={image.id || idx}
                                      sx={{
                                        width: '100%',
                                        minWidth: 180,
                                        minHeight: 180,
                                        height: 'auto',
                                        borderRadius: 1.5,
                                        overflow: 'hidden',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: 1,
                                        position: 'relative',
                                        bgcolor: 'background.paper',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto'
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={image.url}
                                        alt={image.name || `Product image ${idx + 1}`}
                                        sx={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'contain'
                                        }}
                                      />
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleRemoveAsset(image.id);
                                        }}
                                        sx={{
                                          position: 'absolute',
                                          top: 6,
                                          right: 6,
                                          bgcolor: 'rgba(255,255,255,0.8)',
                                          '&:hover': {
                                            bgcolor: 'rgba(255,255,255,1)'
                                          }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            );
                          })()}
                        </Box>
                      </Box>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Keep this information synced with your drop configuration. Use the actions below to save drafts or mark the look ready when details are final.
                    </Typography>
                    {dropCycleOptions.length === 0 ? (
                      <Alert severity="warning">
                        No drop cycles have been configured yet. Head to Planning → Brand Planning to define your calendar rhythm so launches stay coordinated.
                      </Alert>
                    ) : (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'action.hover',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              DROP CYCLE (from Kam Hero Header)
                            </Typography>
                            <Chip
                              label="Read-only"
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Stack>
                          {activeDropCycle ? (
                            <Stack spacing={0.5}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {activeDropCycle.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {describeDropCycle(activeDropCycle)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                To change the drop cycle, use the bimester selector in the Kam Hero Header above.
                              </Typography>
                            </Stack>
                          ) : (
                            <Alert severity="info" sx={{ py: 0.5 }}>
                              No drop cycle selected. Use the bimester selector in the Kam Hero Header above to assign one.
                            </Alert>
                          )}
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Production Plan — {activeLookName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Capture sourcing decisions, cost structure, and build approach for this look.
                      </Typography>
                    </Box>

                    {dropLoading && !dropConfig && <LinearProgress />}

                    {dropConfig ? (
                      <Stack spacing={3}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            color={dropConfig.status === 'ready' ? 'success' : 'default'}
                            label={dropConfig.status === 'ready' ? 'Ready' : 'Draft'}
                          />
                          {dropDirty && (
                            <Typography variant="caption" color="warning.main">
                              Unsaved changes
                            </Typography>
                          )}
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
                          <Stack spacing={1} sx={{ width: { xs: '100%', sm: 200 } }}>
                            <TextField
                              label="Units Planned"
                              type="number"
                              value={dropConfig.units ?? ''}
                              onChange={handleDropUnitsChange}
                              size="small"
                              inputProps={{ min: 0 }}
                              helperText={unitsHelperText}
                              error={Boolean(unitGuardrailWarning)}
                              disabled={dropConfig.unitsLocked}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Boolean(dropConfig.unitsLocked)}
                                  onChange={handleUnitsLockToggle}
                                  size="small"
                                />
                              }
                              label="Lock allocation"
                            />
                          </Stack>
                          <TextField
                            select
                            label="Build Approach"
                            value={dropConfig.mode || 'wholesale'}
                            onChange={handleDropModeChange}
                            size="small"
                            sx={{ width: { xs: '100%', sm: 240 } }}
                          >
                            <MenuItem value="wholesale">Wholesale (ready-to-ship)</MenuItem>
                            <MenuItem value="cut_and_sew">Cut &amp; Sew (new build)</MenuItem>
                            <MenuItem value="knitup">Knitup (knit manufacturing)</MenuItem>
                          </TextField>
                        </Stack>

                        {dropConfig.mode !== 'cut_and_sew' && dropConfig.mode !== 'knitup' && (
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                Wholesale Sourcing
                              </Typography>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setCatalogBrowserOpen(true)}
                                startIcon={<StoreIcon />}
                              >
                                Browse Catalog
                              </Button>
                            </Box>

                            {/* Show readonly fields if selected from catalog browser, otherwise show dropdowns */}
                            {dropConfig.wholesale?.catalogItemName ? (
                              /* Selected from Catalog Browser - Show readonly with change button */
                              <Stack spacing={2}>
                                <Alert severity="success" icon={<CheckCircleIcon />}>
                                  Selected from wholesale catalog
                                </Alert>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                  <TextField
                                    label="Supplier"
                                    value={dropConfig.wholesale?.supplierName || dropConfig.wholesale?.supplierId || ''}
                                    fullWidth
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                    helperText={`Lead time: ${dropConfig.wholesale?.leadTimeDays || '-'} days`}
                                  />
                                  <TextField
                                    label="Catalog Item"
                                    value={dropConfig.wholesale?.catalogItemName || ''}
                                    fullWidth
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                    helperText={`SKU: ${dropConfig.wholesale?.catalogItemId || '-'}`}
                                  />
                                </Stack>
                                <Button
                                  variant="outlined"
                                  onClick={() => setCatalogBrowserOpen(true)}
                                  size="small"
                                  sx={{ alignSelf: 'flex-start' }}
                                >
                                  Change Selection
                                </Button>
                              </Stack>
                            ) : (
                              /* Use dropdown selectors for manual/legacy selection */
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <TextField
                                  select
                                  label="Preferred Supplier"
                                  value={dropConfig.wholesale?.supplierId || ''}
                                  onChange={handleWholesaleSupplierChange}
                                  fullWidth
                                  size="small"
                                  disabled={catalogLoading}
                                  helperText={
                                    selectedSupplier
                                      ? `Lead ${selectedSupplier.leadTimeDays ?? '-'} days • MOQ ${selectedSupplier.moq ?? 'n/a'}`
                                      : 'Select a wholesale partner'
                                  }
                                  SelectProps={{ displayEmpty: true }}
                                >
                                  <MenuItem value="">
                                    <em>Select supplier</em>
                                  </MenuItem>
                                  {wholesaleCatalog.map((supplier) => (
                                    <MenuItem key={supplier.supplierId} value={supplier.supplierId}>
                                      {supplier.name}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <TextField
                                  select
                                  label="Catalog Item"
                                  value={dropConfig.wholesale?.catalogItemId || ''}
                                  onChange={handleWholesaleItemChange}
                                  fullWidth
                                  size="small"
                                  disabled={catalogLoading || !dropConfig.wholesale?.supplierId}
                                  helperText={
                                    selectedCatalogItem
                                      ? `${formatCurrency(selectedCatalogItem.unitCost)} • ${selectedCatalogItem.fabric || selectedCatalogItem.categoryLabels?.join(', ')}`
                                      : 'Choose a silhouette'
                                  }
                                  SelectProps={{ displayEmpty: true }}
                                >
                                  <MenuItem value="">
                                    <em>Select silhouette</em>
                                  </MenuItem>
                                  {supplierCatalogItems.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                      {item.name} — {formatCurrency(item.unitCost)}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Stack>
                            )}

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                              <TextField
                                label="Unit Cost"
                                type="number"
                                size="small"
                                value={dropConfig.wholesale?.unitCost ?? ''}
                                onChange={handleWholesaleFieldChange('unitCost')}
                                sx={{ width: { xs: '100%', md: 180 } }}
                                inputProps={{ min: 0, step: 0.01 }}
                                helperText="Override with quoted cost if needed."
                              />
                              <TextField
                                label="Lead Time (days)"
                                type="number"
                                size="small"
                                value={dropConfig.wholesale?.leadTimeDays ?? ''}
                                onChange={handleWholesaleFieldChange('leadTimeDays')}
                                sx={{ width: { xs: '100%', md: 180 } }}
                                inputProps={{ min: 0, step: 1 }}
                              />
                              <TextField
                                label="Colorway"
                                size="small"
                                value={dropConfig.wholesale?.colorway || ''}
                                onChange={handleWholesaleFieldChange('colorway')}
                                sx={{ width: { xs: '100%', md: 180 } }}
                                placeholder="e.g. Jet Black"
                              />
                              <TextField
                                label="Size Scale"
                                size="small"
                                value={dropConfig.wholesale?.sizeScale || ''}
                                onChange={handleWholesaleFieldChange('sizeScale')}
                                sx={{ width: { xs: '100%', md: 180 } }}
                                placeholder="XS-3XL"
                              />
                            </Stack>

                            <TextField
                              label="Wholesale Notes"
                              value={dropConfig.wholesale?.notes || ''}
                              onChange={handleWholesaleFieldChange('notes')}
                              fullWidth
                              multiline
                              minRows={2}
                              size="small"
                            />
                          </Stack>
                        )}

                        {dropConfig.mode === 'cut_and_sew' && (
                          <Stack spacing={2}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              Cut &amp; Sew Build
                            </Typography>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                              <TextField
                                select
                                label="Pattern Source"
                                value={dropConfig.cutAndSew?.patternSource || 'existing'}
                                onChange={handlePatternSourceChange}
                                size="small"
                                sx={{ width: { xs: '100%', md: 200 } }}
                                disabled={catalogLoading}
                              >
                                <MenuItem value="existing">Existing library pattern</MenuItem>
                                <MenuItem value="new">New pattern</MenuItem>
                              </TextField>

                              {dropConfig.cutAndSew?.patternSource !== 'new' && (
                                <TextField
                                select
                                label="Pattern"
                                value={dropConfig.cutAndSew?.patternId || ''}
                                onChange={handlePatternSelect}
                                fullWidth
                                size="small"
                                disabled={catalogLoading}
                                helperText={
                                  selectedPattern
                                      ? `${selectedPattern.block || ''} • ${selectedPattern.difficulty || ''}`
                                      : 'Pick a block to start from'
                                  }
                                  SelectProps={{ displayEmpty: true }}
                                >
                                  <MenuItem value="">
                                    <em>Select pattern</em>
                                  </MenuItem>
                                  {patternCatalog.map((pattern) => (
                                    <MenuItem key={pattern.id} value={pattern.id}>
                                      {pattern.name}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              )}

                              {dropConfig.cutAndSew?.patternSource === 'new' && (
                                <TextField
                                  label="Pattern Name"
                                  value={dropConfig.cutAndSew?.patternName || ''}
                                  onChange={handleCutAndSewFieldChange('patternName')}
                                  fullWidth
                                  size="small"
                                  placeholder="Describe the new block"
                                />
                              )}
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                              <TextField
                                select
                                label="Fabric"
                                value={dropConfig.cutAndSew?.fabricId || ''}
                                onChange={handleFabricSelect}
                                fullWidth
                                size="small"
                                disabled={catalogLoading}
                                helperText="Recommended fabrics appear first."
                                SelectProps={{ displayEmpty: true }}
                              >
                                <MenuItem value="">
                                  <em>Select fabric</em>
                                </MenuItem>
                                {fabricOptions.map((fabric) => (
                                  <MenuItem key={fabric.id} value={fabric.id}>
                                    {fabric.name}
                                    {fabric.source === 'recommended' ? ' • recommended' : ''}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                label="Fabric Cost / Unit"
                                type="number"
                                size="small"
                                value={dropConfig.cutAndSew?.fabricCostPerUnit ?? ''}
                                onChange={handleCutAndSewFieldChange('fabricCostPerUnit')}
                                sx={{ width: { xs: '100%', md: 200 } }}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                              <TextField
                                label="Labor Cost / Unit"
                                type="number"
                                size="small"
                                value={dropConfig.cutAndSew?.laborCostPerUnit ?? ''}
                                onChange={handleCutAndSewFieldChange('laborCostPerUnit')}
                                sx={{ width: { xs: '100%', md: 200 } }}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                              <TextField
                                label="Trims Cost / Unit"
                                type="number"
                                size="small"
                                value={dropConfig.cutAndSew?.trimsCostPerUnit ?? ''}
                                onChange={handleCutAndSewFieldChange('trimsCostPerUnit')}
                                sx={{ width: { xs: '100%', md: 200 } }}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Stack>

                            <TextField
                              label="Cut &amp; Sew Notes"
                              value={dropConfig.cutAndSew?.notes || ''}
                              onChange={handleCutAndSewFieldChange('notes')}
                              fullWidth
                              multiline
                              minRows={2}
                              size="small"
                            />
                          </Stack>
                        )}

                        {dropConfig.mode === 'knitup' && (
                          <Stack spacing={2}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              Knitup Manufacturing
                            </Typography>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                              <TextField
                                label="Product Template"
                                value={dropConfig.knitup?.productTemplateName || ''}
                                onChange={(e) => {
                                  setDropConfig(prev => ({
                                    ...prev,
                                    knitup: {
                                      ...prev.knitup,
                                      productTemplateName: e.target.value
                                    }
                                  }));
                                }}
                                fullWidth
                                size="small"
                                placeholder="e.g., Basic Crew Neck Sweater"
                              />
                              <TextField
                                label="Base Cost per Unit"
                                type="number"
                                value={dropConfig.knitup?.baseCost || ''}
                                onChange={(e) => {
                                  setDropConfig(prev => ({
                                    ...prev,
                                    knitup: {
                                      ...prev.knitup,
                                      baseCost: parseFloat(e.target.value) || 0
                                    }
                                  }));
                                }}
                                size="small"
                                sx={{ width: { xs: '100%', md: 180 } }}
                                inputProps={{ min: 0, step: 0.01 }}
                                InputProps={{ startAdornment: '$' }}
                              />
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                              <TextField
                                label="Yarn Type"
                                value={dropConfig.knitup?.yarnType || ''}
                                onChange={(e) => {
                                  setDropConfig(prev => ({
                                    ...prev,
                                    knitup: {
                                      ...prev.knitup,
                                      yarnType: e.target.value
                                    }
                                  }));
                                }}
                                size="small"
                                sx={{ flex: 1 }}
                                placeholder="e.g., Merino Wool"
                              />
                              <TextField
                                label="Yarn Weight"
                                value={dropConfig.knitup?.yarnWeight || ''}
                                onChange={(e) => {
                                  setDropConfig(prev => ({
                                    ...prev,
                                    knitup: {
                                      ...prev.knitup,
                                      yarnWeight: e.target.value
                                    }
                                  }));
                                }}
                                size="small"
                                sx={{ flex: 1 }}
                                placeholder="e.g., Worsted"
                              />
                              <TextField
                                label="Gauge"
                                value={dropConfig.knitup?.gauge || ''}
                                onChange={(e) => {
                                  setDropConfig(prev => ({
                                    ...prev,
                                    knitup: {
                                      ...prev.knitup,
                                      gauge: e.target.value
                                    }
                                  }));
                                }}
                                size="small"
                                sx={{ flex: 1 }}
                                placeholder="e.g., 7gg"
                              />
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                              <TextField
                                label="Construction"
                                value={dropConfig.knitup?.construction || ''}
                                onChange={(e) => {
                                  setDropConfig(prev => ({
                                    ...prev,
                                    knitup: {
                                      ...prev.knitup,
                                      construction: e.target.value
                                    }
                                  }));
                                }}
                                size="small"
                                sx={{ flex: 1 }}
                                placeholder="e.g., Fully-fashioned"
                              />
                              <TextField
                                label="Lead Time (days)"
                                type="number"
                                value={dropConfig.knitup?.leadTimeDays || ''}
                                onChange={(e) => {
                                  setDropConfig(prev => ({
                                    ...prev,
                                    knitup: {
                                      ...prev.knitup,
                                      leadTimeDays: parseInt(e.target.value) || 0
                                    }
                                  }));
                                }}
                                size="small"
                                sx={{ width: { xs: '100%', md: 140 } }}
                                inputProps={{ min: 0 }}
                              />
                              <TextField
                                label="Min Order Qty"
                                type="number"
                                value={dropConfig.knitup?.minimumOrderQuantity || ''}
                                onChange={(e) => {
                                  setDropConfig(prev => ({
                                    ...prev,
                                    knitup: {
                                      ...prev.knitup,
                                      minimumOrderQuantity: parseInt(e.target.value) || 0
                                    }
                                  }));
                                }}
                                size="small"
                                sx={{ width: { xs: '100%', md: 140 } }}
                                inputProps={{ min: 0 }}
                              />
                            </Stack>

                            <TextField
                              label="Notes"
                              value={dropConfig.knitup?.notes || ''}
                              onChange={(e) => {
                                setDropConfig(prev => ({
                                  ...prev,
                                  knitup: {
                                    ...prev.knitup,
                                    notes: e.target.value
                                  }
                                }));
                              }}
                              fullWidth
                              multiline
                              minRows={2}
                              size="small"
                            />
                          </Stack>
                        )}

                        <Divider flexItem />

                        <Stack spacing={2}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Budget Overrides
                          </Typography>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                              label="Override Total COGS Budget"
                              type="number"
                              size="small"
                              value={dropConfig.budgetOverrides?.totalCogs ?? ''}
                              onChange={handleBudgetOverrideChange('totalCogs')}
                              sx={{ width: { xs: '100%', md: 220 } }}
                              inputProps={{ min: 0, step: 1 }}
                              helperText="Optional, requires approval."
                            />
                            <TextField
                              label="Materials Share"
                              type="number"
                              size="small"
                              value={dropConfig.budgetOverrides?.materialsShare ?? ''}
                              onChange={handleBudgetOverrideChange('materialsShare')}
                              sx={{ width: { xs: '100%', md: 200 } }}
                              inputProps={{ min: 0, max: 1, step: 0.05 }}
                              helperText="0 – 1"
                            />
                            <TextField
                              label="Labor Share"
                              type="number"
                              size="small"
                              value={dropConfig.budgetOverrides?.laborShare ?? ''}
                              onChange={handleBudgetOverrideChange('laborShare')}
                              sx={{ width: { xs: '100%', md: 200 } }}
                              inputProps={{ min: 0, max: 1, step: 0.05 }}
                              helperText="0 – 1"
                            />
                          </Stack>
                        </Stack>

                        <Divider flexItem />

                        <Stack spacing={2}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Ideogram Prompt &amp; References
                          </Typography>
                          <TextField
                            label="Prompt"
                            value={dropConfig?.assets?.prompt || ''}
                            onChange={handleAssetsChange('prompt')}
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            placeholder="Detailed prompt for this look..."
                          />
                          <TextField
                            label="Negative Prompt"
                            value={dropConfig?.assets?.negativePrompt || ''}
                            onChange={handleAssetsChange('negativePrompt')}
                            fullWidth
                            size="small"
                            placeholder="low quality, blurry, distorted..."
                          />
                          <TextField
                            label="Reference Images (URLs, one per line)"
                            value={(dropConfig?.assets?.referenceImages || []).join('\n')}
                            onChange={handleAssetsChange('referenceImages')}
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleApplyPromptFromConfig}
                            disabled={!dropConfig?.assets?.prompt && !dropConfig?.assets?.negativePrompt}
                          >
                            Apply to Prompt Panel
                          </Button>
                        </Stack>

                        <Divider flexItem />

                        <TextField
                          label="Internal Notes"
                          value={dropConfig.notes || ''}
                          onChange={handleDropNotesChange}
                          fullWidth
                          multiline
                          minRows={2}
                          size="small"
                          placeholder="Record approvals, supplier confirmations, or open questions."
                        />
                      </Stack>
                    ) : (
                      <Stack spacing={1}>
                        {dropLoading ? (
                          <>
                            <LinearProgress />
                            <Typography variant="body2" color="text.secondary">
                              Loading drop configuration…
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No configuration saved yet. Start by choosing a supplier or a cut &amp; sew path for this look.
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleDropRefresh}
                    disabled={dropLoading || dropSaving}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={() => handleDropSave('draft')}
                    disabled={
                      dropLoading ||
                      dropSaving ||
                      !dropConfig ||
                      (!dropDirty && dropConfig.status === 'draft')
                    }
                  >
                    {dropSaving && dropConfig?.status === 'draft'
                      ? 'Saving…'
                      : dropConfig?.status === 'draft' && !dropDirty
                      ? 'Draft Saved'
                      : 'Save Draft'}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={() => handleDropSave('ready')}
                    disabled={
                      dropLoading ||
                      dropSaving ||
                      !dropConfig ||
                      (dropConfig.status === 'ready' && !dropDirty)
                    }
                  >
                    {dropSaving && dropConfig?.status === 'ready'
                      ? 'Saving…'
                      : dropConfig?.status === 'ready'
                      ? dropDirty
                        ? 'Save Changes'
                        : 'Ready'
                      : dropDirty
                      ? 'Save & Mark Ready'
                      : 'Mark Ready'}
                  </Button>
                </CardActions>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        label="Product Name"
                        value={dropConfig.productInfo?.name || ''}
                        onChange={handleProductInfoChange('name')}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="SKU / Style ID"
                        value={dropConfig.productInfo?.sku || ''}
                        onChange={handleProductInfoChange('sku')}
                        fullWidth
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button size="small" onClick={handleGenerateSku}>
                                Auto
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        label="Season"
                        value={dropConfig.productInfo?.season || ''}
                        onChange={handleProductInfoChange('season')}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Collection"
                        value={dropConfig.productInfo?.collection || ''}
                        onChange={handleProductInfoChange('collection')}
                        fullWidth
                        size="small"
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        label="Color Code"
                        value={dropConfig.productInfo?.colorCode || ''}
                        onChange={handleProductInfoChange('colorCode')}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Color Description"
                        value={dropConfig.productInfo?.colorDescription || ''}
                        onChange={handleProductInfoChange('colorDescription')}
                        fullWidth
                        size="small"
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        label="Fit / Silhouette"
                        value={dropConfig.productInfo?.fit || ''}
                        onChange={handleProductInfoChange('fit')}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Size Run / Sizing Notes"
                        value={dropConfig.productInfo?.sizeRun || ''}
                        onChange={handleProductInfoChange('sizeRun')}
                        fullWidth
                        size="small"
                      />
                    </Stack>
                    <TextField
                      label="Material Information"
                      value={dropConfig.productInfo?.material || ''}
                      onChange={handleProductInfoChange('material')}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      placeholder="e.g., 240GSM organic cotton jersey, discharge print, rib collar"
                    />
                    <TextField
                      label="Technical Notes"
                      value={dropConfig.productInfo?.technicalNotes || ''}
                      onChange={handleProductInfoChange('technicalNotes')}
                      fullWidth
                      multiline
                      minRows={3}
                      size="small"
                      placeholder="Construction details, fit adjustments, component specs..."
                    />
                    <TextField
                      label="Production Notes"
                      value={dropConfig.productInfo?.productionNotes || ''}
                      onChange={handleProductInfoChange('productionNotes')}
                      fullWidth
                      multiline
                      minRows={3}
                      size="small"
                      placeholder="MOQ, lead time agreements, embellishment callouts, packaging requirements..."
                    />
                    <TextField
                      label="Care Instructions"
                      value={dropConfig.productInfo?.careInstructions || ''}
                      onChange={handleProductInfoChange('careInstructions')}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      placeholder="Machine wash cold, tumble dry low, do not iron print..."
                    />
                    {/* Mode-Specific Cost Forms */}
                    {dropConfig.mode === 'cut_and_sew' ? (
                      <CutAndSewCostForm
                        costs={dropConfig.productInfo?.costs || {}}
                        onChange={handleProductInfoCostsChange}
                      />
                    ) : (
                      <WhiteLabelCostForm
                        costs={dropConfig.productInfo?.costs || {}}
                        partnerQuotes={dropConfig.productInfo?.partnerQuotes}
                        selectedPartnerQuote={dropConfig.productInfo?.selectedPartnerQuote}
                        onChange={handleProductInfoCostsChange}
                      />
                    )}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        label="Launch Date"
                        type="date"
                        size="small"
                        value={toDateInputValue(dropConfig.productInfo?.launchDate)}
                        onChange={handleProductInfoDateChange('launchDate')}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <TextField
                        label="Ship Date"
                        type="date"
                        size="small"
                        value={toDateInputValue(dropConfig.productInfo?.shipDate)}
                        onChange={handleProductInfoDateChange('shipDate')}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    </Stack>
                    <Box
                      sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        pt: 2,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        {dropConfig.mode === 'cut_and_sew' ? 'Cut & Sew Tech Pack' : 'White Label Tech Pack'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {dropConfig.mode === 'cut_and_sew'
                          ? 'Capture the metadata, materials, construction, and artwork notes that map to the SIVAD_GARMENT_01 tech pack schema.'
                          : 'Define blank program metadata, embellishments, and packaging requirements using the SIVAD_GARMENT_01B schema.'}
                      </Typography>
                      {dropConfig.mode === 'cut_and_sew' ? (
                        <CutAndSewSchemaForm
                          schema={activeSchema}
                          onChange={handleSchemaChange}
                          brandName={brandDisplayName}
                        />
                      ) : (
                        <WhiteLabelSchemaForm
                          schema={activeSchema}
                          onChange={handleSchemaChange}
                          brandName={brandDisplayName}
                        />
                      )}
                    </Box>
                    <Alert severity="info">
                      Draft saves capture in-progress updates; ready saves mark the look as production-ready.
                    </Alert>
                  </Stack>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={() => handleDropSave('draft')}
                    disabled={
                      dropLoading ||
                      dropSaving ||
                      !dropConfig ||
                      (!dropDirty && dropConfig.status === 'draft')
                    }
                  >
                    {dropSaving && dropConfig?.status === 'draft'
                      ? 'Saving…'
                      : dropConfig?.status === 'draft' && !dropDirty
                      ? 'Draft Saved'
                      : 'Save Draft'}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={() => handleDropSave('ready')}
                    disabled={
                      dropLoading ||
                      dropSaving ||
                      !dropConfig ||
                      (dropConfig.status === 'ready' && !dropDirty)
                    }
                  >
                    {dropSaving && dropConfig?.status === 'ready'
                      ? 'Saving…'
                      : dropConfig?.status === 'ready'
                      ? dropDirty
                        ? 'Save Changes'
                        : 'Ready'
                      : dropDirty
                      ? 'Save & Mark Ready'
                      : 'Mark Ready'}
                  </Button>
                </CardActions>
              </Card>
              </>
            ) : (
              <Alert severity="info">
                Drop configuration is not loaded yet. Refresh the drop workspace to sync product details.
              </Alert>
            )}
          </Stack>
        )}

        {activeNav === 'lookbook' && committedLooks.length === 0 && (
          <Paper
            variant="outlined"
            sx={{
              mb: 2,
              borderRadius: 2,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Lookbook Workspace
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Curate campaign-ready storyboards and export shot lists. This workspace is coming soon.
            </Typography>
          </Paper>
        )}

        {activeNav === 'archive' && committedLooks.length === 0 && (
          <Paper
            variant="outlined"
            sx={{
              mb: 2,
              borderRadius: 2,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Asset Archive
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Store final renders, reference prompts, and production notes. Archiving will be activated in a later release.
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Save Design Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Save Design
        </DialogTitle>
        <DialogContent>
          <input
            type="text"
            value={designName}
            onChange={(e) => setDesignName(e.target.value)}
            placeholder="e.g., Summer Collection T-Shirt"
            style={{
              width: '100%',
              padding: '12px',
              marginTop: '16px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDesign}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Wholesale Catalog Browser Dialog */}
      <WholesaleCatalogBrowser
        open={catalogBrowserOpen}
        onClose={() => setCatalogBrowserOpen(false)}
        onSelect={handleCatalogItemSelected}
      />
    </Box>
  );
};

const SectionCard = ({ title, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      mb: 2,
      borderRadius: 2,
    }}
  >
    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
      {title}
    </Typography>
    {children}
  </Paper>
);

const CutAndSewSchemaForm = ({ schema, onChange, brandName }) => {
  const data = schema || createDefaultCutAndSewSchema();
  const metadata = data.metadata || {};
  const mainFabric = data.construction?.materials?.main_fabric || {};
  const placements = Array.isArray(data.artwork?.placements) ? data.artwork.placements : [];
  const styleKeywordValue = Array.isArray(data.ai_generation?.style_keywords)
    ? data.ai_generation.style_keywords.join(', ')
    : '';
  const csBrandLabel = data.labeling_packaging?.brand_label || {};
  const csCareLabel = data.labeling_packaging?.care_label || {};
  const csPackaging = data.labeling_packaging?.packaging || {};
  const currentCareStrategy = normalizeCareStrategyValue(csCareLabel.strategy) || '';

  const handleMetadataField = (field, summaryKey) => (event) => {
    const value = event.target.value;
    const summaryUpdates = summaryKey ? { [summaryKey]: value } : null;
    onChange(
      (current) => ({
        ...current,
        metadata: {
          ...(current.metadata || {}),
          [field]: value,
        },
      }),
      summaryUpdates
    );
  };

  const handleMainFabricField = (field) => (event) => {
    const value = event.target.value;
    onChange(
      (current) => ({
        ...current,
        construction: {
          ...(current.construction || {}),
          materials: {
            ...(current.construction?.materials || {}),
            main_fabric: {
              ...(current.construction?.materials?.main_fabric || {}),
              [field]: value,
            },
          },
        },
      }),
      field === 'composition' ? { material: value } : null
    );
  };

  const handlePlacementChange = (index, field) => (event) => {
    const value = event.target.value;
    onChange((current) => {
      const list = Array.isArray(current.artwork?.placements) ? current.artwork.placements.slice() : [];
      list[index] = {
        ...(list[index] || {}),
        [field]: value,
      };
      return {
        ...current,
        artwork: {
          ...(current.artwork || {}),
          placements: list,
        },
      };
    });
  };

  const handleAddPlacement = () => {
    onChange((current) => {
      const list = Array.isArray(current.artwork?.placements) ? current.artwork.placements : [];
      return {
        ...current,
        artwork: {
          ...(current.artwork || {}),
          placements: [...list, { location: '', technique: '', dimensions: '', thread_color: '' }],
        },
      };
    });
  };

  const handleRemovePlacement = (index) => () => {
    onChange((current) => {
      const list = Array.isArray(current.artwork?.placements) ? current.artwork.placements.slice() : [];
      list.splice(index, 1);
      return {
        ...current,
        artwork: {
          ...(current.artwork || {}),
          placements: list,
        },
      };
    });
  };

  const handleStyleKeywords = (event) => {
    const keywords = event.target.value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
    onChange((current) => ({
      ...current,
      ai_generation: {
        ...(current.ai_generation || {}),
        style_keywords: keywords,
      },
    }));
  };

  const handleAIGenerationField = (field) => (event) => {
    const value = event.target.value;
    onChange((current) => ({
      ...current,
      ai_generation: {
        ...(current.ai_generation || {}),
        [field]: value,
      },
    }));
  };

  const handleLabelingField = (segment, field, summaryKey) => (event) => {
    const value = event.target.value;
    const summaryUpdates = summaryKey ? { [summaryKey]: value } : null;
    onChange(
      (current) => ({
        ...current,
        labeling_packaging: {
          ...(current.labeling_packaging || {}),
          [segment]: {
            ...(current.labeling_packaging?.[segment] || {}),
            [field]: value,
          },
        },
      }),
      summaryUpdates
    );
  };

  const handleBrandLabelField = (field) => (event) => {
    const value = event.target.value;
    onChange((current) => ({
      ...current,
      labeling_packaging: {
        ...(current.labeling_packaging || {}),
        brand_label: {
          ...(current.labeling_packaging?.brand_label || {}),
          [field]: value,
        },
      },
    }));
  };

  const handleCareField = (field, summaryKey) => (event, overrideValue) => {
    const rawValue = overrideValue !== undefined ? overrideValue : event?.target?.value ?? '';
    const normalizedValue = field === 'strategy' ? normalizeCareStrategyValue(rawValue) : rawValue;
    const summaryUpdates = summaryKey ? { [summaryKey]: normalizedValue } : null;
    onChange(
      (current) => ({
        ...current,
        labeling_packaging: {
          ...(current.labeling_packaging || {}),
          care_label: {
            ...(current.labeling_packaging?.care_label || {}),
            [field]: normalizedValue,
          },
        },
      }),
      summaryUpdates
    );
  };

  const handlePackagingField = (field) => (event) => {
    const value = event.target.value;
    onChange((current) => ({
      ...current,
      labeling_packaging: {
        ...(current.labeling_packaging || {}),
        packaging: {
          ...(current.labeling_packaging?.packaging || {}),
          [field]: field === 'carton_quantity' ? (value === '' ? '' : Number(value)) : value,
        },
      },
    }));
  };

  const handlePackagingExtrasToggle = (option) => (event) => {
    const checked = event.target.checked;
    onChange((current) => {
      const existing =
        Array.isArray(current.labeling_packaging?.packaging?.extras) &&
        current.labeling_packaging.packaging.extras.length
          ? current.labeling_packaging.packaging.extras.slice()
          : [];
      const nextExtras = checked ? [...new Set([...existing, option])] : existing.filter((item) => item !== option);
      return {
        ...current,
        labeling_packaging: {
          ...(current.labeling_packaging || {}),
          packaging: {
            ...(current.labeling_packaging?.packaging || {}),
            extras: nextExtras,
          },
        },
      };
    });
  };

  const handleNotesField = (field, summaryKey) => (event) => {
    const value = event.target.value;
    const summaryUpdates = summaryKey ? { [summaryKey]: value } : null;
    onChange(
      (current) => ({
        ...current,
        notes: {
          ...(current.notes || {}),
          [field]: value,
        },
      }),
      summaryUpdates
    );
  };

  return (
    <Stack spacing={2}>
      <SectionCard title="Metadata">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Garment Name"
            value={metadata.garment_name || ''}
            onChange={handleMetadataField('garment_name', 'name')}
            size="small"
            fullWidth
          />
          <TextField
            label="Style Code"
            value={metadata.style_code || ''}
            onChange={handleMetadataField('style_code', 'sku')}
            size="small"
            fullWidth
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Collection"
            value={metadata.collection || ''}
            onChange={handleMetadataField('collection', 'collection')}
            size="small"
            fullWidth
          />
          <TextField
            label="Season"
            value={metadata.season || ''}
            onChange={handleMetadataField('season', 'season')}
            size="small"
            fullWidth
          />
        </Stack>
      </SectionCard>

      <SectionCard title="Main Fabric">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Fabric Name"
            value={mainFabric.name || ''}
            onChange={handleMainFabricField('name')}
            size="small"
            fullWidth
          />
          <TextField
            label="Composition"
            value={mainFabric.composition || ''}
            onChange={handleMainFabricField('composition')}
            size="small"
            fullWidth
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Weight"
            value={mainFabric.weight || ''}
            onChange={handleMainFabricField('weight')}
            size="small"
            fullWidth
          />
          <TextField
            label="Color"
            value={mainFabric.color || ''}
            onChange={handleMainFabricField('color')}
            size="small"
            fullWidth
          />
          <TextField
            label="Supplier"
            value={mainFabric.supplier || ''}
            onChange={handleMainFabricField('supplier')}
            size="small"
            fullWidth
          />
        </Stack>
      </SectionCard>

      <SectionCard title="Labeling & Packaging">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label="Brand Label Execution"
            value={csBrandLabel.execution || ''}
            onChange={handleBrandLabelField('execution')}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>Select execution</em>
            </MenuItem>
            {BRAND_LABEL_EXECUTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Brand Label Location"
            value={csBrandLabel.location || ''}
            onChange={handleBrandLabelField('location')}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>Select location</em>
            </MenuItem>
            {BRAND_LABEL_LOCATIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        {csBrandLabel.location === 'Other' && (
          <TextField
            label="Custom Location"
            value={csBrandLabel.custom_location || ''}
            onChange={handleBrandLabelField('custom_location')}
            size="small"
            fullWidth
            sx={{ mt: 2 }}
          />
        )}
        <TextField
          label="Label Content / Notes"
          value={csBrandLabel.content || ''}
          onChange={handleBrandLabelField('content')}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            select
            label="Care Label Strategy"
            value={currentCareStrategy}
            onChange={(event) => handleCareField('strategy')(event)}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>Select strategy</em>
            </MenuItem>
            {CARE_STRATEGIES.map((option) => (
              <MenuItem key={option} value={option}>
                {getCareStrategyLabel(option, brandName)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Care Label Languages"
            value={csCareLabel.languages || []}
            onChange={(event) => handleCareField('languages')(null, event.target.value)}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
            }}
            size="small"
            fullWidth
          >
            {CARE_LANGUAGES.map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={Array.isArray(csCareLabel.languages) && csCareLabel.languages.includes(option)} />
                <Typography variant="body2">{option}</Typography>
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <TextField
          label="Care Instructions"
          value={csCareLabel.instructions || ''}
          onChange={(event) => handleCareField('instructions', 'careInstructions')(event)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
        <TextField
          label="Compliance / Fiber Notes"
          value={csCareLabel.custom_notes || ''}
          onChange={(event) => handleCareField('custom_notes')(event)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            select
            label="Fold Method"
            value={csPackaging.fold_method || ''}
            onChange={handlePackagingField('fold_method')}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>Select method</em>
            </MenuItem>
            {PACKAGING_FOLD_METHODS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Bag Type"
            value={csPackaging.bag_type || ''}
            onChange={handlePackagingField('bag_type')}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>Select bag</em>
            </MenuItem>
            {PACKAGING_BAG_TYPES.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Carton Quantity"
            type="number"
            value={csPackaging.carton_quantity ?? ''}
            onChange={handlePackagingField('carton_quantity')}
            size="small"
            fullWidth
          />
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
          {PACKAGING_EXTRAS.map((extra) => (
            <FormControlLabel
              key={extra}
              control={
                <Checkbox
                  size="small"
                  checked={Array.isArray(csPackaging.extras) && csPackaging.extras.includes(extra)}
                  onChange={handlePackagingExtrasToggle(extra)}
                />
              }
              label={extra}
            />
          ))}
        </Stack>
        <TextField
          label="Packaging Notes"
          value={csPackaging.notes || ''}
          onChange={handlePackagingField('notes')}
          size="small"
          fullWidth
          multiline
          minRows={2}
        />
      </SectionCard>

      <SectionCard title="Artwork Placements">
        <Stack spacing={1}>
          {placements.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Document embroidery, prints, or applique details here.
            </Typography>
          )}
          {placements.map((placement, index) => (
            <Stack
              key={`placement-${index}`}
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems="flex-start"
              sx={{ border: '1px dashed', borderColor: 'divider', p: 1.5, borderRadius: 1 }}
            >
              <TextField
                label="Location"
                value={placement.location || ''}
                onChange={handlePlacementChange(index, 'location')}
                size="small"
                fullWidth
              />
              <TextField
                label="Technique"
                value={placement.technique || ''}
                onChange={handlePlacementChange(index, 'technique')}
                size="small"
                fullWidth
              />
              <TextField
                label="Dimensions"
                value={placement.dimensions || ''}
                onChange={handlePlacementChange(index, 'dimensions')}
                size="small"
                fullWidth
              />
              <TextField
                label="Thread/Color"
                value={placement.thread_color || ''}
                onChange={handlePlacementChange(index, 'thread_color')}
                size="small"
                fullWidth
              />
              <IconButton size="small" onClick={handleRemovePlacement(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button startIcon={<AddIcon />} onClick={handleAddPlacement} variant="outlined" size="small">
            Add Placement
          </Button>
        </Stack>
      </SectionCard>

      <SectionCard title="AI Guidance & Notes">
        <TextField
          label="Style Keywords"
          value={styleKeywordValue}
          onChange={handleStyleKeywords}
          helperText="Comma separated list, e.g., oversized, minimal, utilitarian"
          size="small"
          fullWidth
        />
        <TextField
          label="Background Style"
          value={data.ai_generation?.background_style || ''}
          onChange={handleAIGenerationField('background_style')}
          size="small"
          fullWidth
          sx={{ mt: 2 }}
        />
        <TextField
          label="Factory Instructions"
          value={data.notes?.factory_instructions || ''}
          onChange={handleNotesField('factory_instructions', 'technicalNotes')}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
        <TextField
          label="Designer Notes"
          value={data.notes?.designer_notes || ''}
          onChange={handleNotesField('designer_notes', 'productionNotes')}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
      </SectionCard>
    </Stack>
  );
};

const WhiteLabelSchemaForm = ({ schema, onChange, brandName }) => {
  const data = schema || createDefaultWhiteLabelSchema();
  const metadata = data.metadata || {};
  const visualAssets = data.visual_assets || {};
  const colorSwatches = Array.isArray(visualAssets.color_swatches) ? visualAssets.color_swatches : [];
  const embellishments = Array.isArray(data.embellishments) ? data.embellishments : [];
  const styleKeywordValue = Array.isArray(data.ai_generation?.style_keywords)
    ? data.ai_generation.style_keywords.join(', ')
    : '';
  const labeling = data.labeling_packaging || {};
  const packaging = labeling.packaging || {};
  const whiteLabelCareStrategy =
    typeof labeling.care_label === 'string' ? normalizeCareStrategyValue(labeling.care_label) || '' : '';

  const handleMetadataField = (field, summaryKey) => (event) => {
    const value = event.target.value;
    const summaryUpdates = summaryKey ? { [summaryKey]: value } : null;
    onChange(
      (current) => ({
        ...current,
        metadata: {
          ...(current.metadata || {}),
          [field]: value,
        },
      }),
      summaryUpdates
    );
  };

  const handleVisualAssetField = (field) => (event) => {
    const value = event.target.value;
    onChange((current) => ({
      ...current,
      visual_assets: {
        ...(current.visual_assets || {}),
        [field]: value,
      },
    }));
  };

  const handleSwatchChange = (index, field) => (event) => {
    const value = event.target.value;
    onChange((current) => {
      const list = Array.isArray(current.visual_assets?.color_swatches)
        ? current.visual_assets.color_swatches.slice()
        : [];
      list[index] = {
        ...(list[index] || {}),
        [field]: value,
      };
      return {
        ...current,
        visual_assets: {
          ...(current.visual_assets || {}),
          color_swatches: list,
        },
      };
    });
  };

  const handleAddSwatch = () => {
    onChange((current) => {
      const list = Array.isArray(current.visual_assets?.color_swatches)
        ? current.visual_assets.color_swatches
        : [];
      return {
        ...current,
        visual_assets: {
          ...(current.visual_assets || {}),
          color_swatches: [...list, { color_name: '', pantone_code: '' }],
        },
      };
    });
  };

  const handleRemoveSwatch = (index) => () => {
    onChange((current) => {
      const list = Array.isArray(current.visual_assets?.color_swatches)
        ? current.visual_assets.color_swatches.slice()
        : [];
      list.splice(index, 1);
      return {
        ...current,
        visual_assets: {
          ...(current.visual_assets || {}),
          color_swatches: list,
        },
      };
    });
  };

  const handleEmbellishmentChange = (index, field) => (event) => {
    const value = event.target.value;
    onChange((current) => {
      const list = Array.isArray(current.embellishments) ? current.embellishments.slice() : [];
      list[index] = {
        ...(list[index] || {}),
        [field]: value,
      };
      return {
        ...current,
        embellishments: list,
      };
    });
  };

  const handleAddEmbellishment = () => {
    onChange((current) => {
      const list = Array.isArray(current.embellishments) ? current.embellishments : [];
      return {
        ...current,
        embellishments: [
          ...list,
          { type: '', placement: '', artwork_url: '', dimensions: '', thread_color: '' },
        ],
      };
    });
  };

  const handleRemoveEmbellishment = (index) => () => {
    onChange((current) => {
      const list = Array.isArray(current.embellishments) ? current.embellishments.slice() : [];
      list.splice(index, 1);
      return {
        ...current,
        embellishments: list,
      };
    });
  };

  const handleLabelingField = (field, summaryKey) => (event) => {
    let value = event.target.value;
    if (field === 'care_label') {
      value = normalizeCareStrategyValue(value) || '';
    }
    const summaryUpdates = summaryKey ? { [summaryKey]: value } : null;
    onChange(
      (current) => ({
        ...current,
        labeling_packaging: {
          ...(current.labeling_packaging || {}),
          [field]: value,
        },
      }),
      summaryUpdates
    );
  };

  const handlePackagingField = (field) => (event) => {
    const value = event.target.value;
    onChange((current) => ({
      ...current,
      labeling_packaging: {
        ...(current.labeling_packaging || {}),
        packaging: {
          ...(current.labeling_packaging?.packaging || {}),
          [field]: value,
        },
      },
    }));
  };

  const handlePackagingExtrasToggle = (option) => (event) => {
    const checked = event.target.checked;
    onChange((current) => {
      const existing =
        Array.isArray(current.labeling_packaging?.packaging?.extras) &&
        current.labeling_packaging.packaging.extras.length
          ? current.labeling_packaging.packaging.extras.slice()
          : [];
      const nextExtras = checked ? [...new Set([...existing, option])] : existing.filter((item) => item !== option);
      return {
        ...current,
        labeling_packaging: {
          ...(current.labeling_packaging || {}),
          packaging: {
            ...(current.labeling_packaging?.packaging || {}),
            extras: nextExtras,
          },
        },
      };
    });
  };

  const handleRelabelToggle = (event) => {
    const checked = event.target.checked;
    onChange((current) => ({
      ...current,
      labeling_packaging: {
        ...(current.labeling_packaging || {}),
        relabel: checked,
      },
    }));
  };

  const handleStyleKeywords = (event) => {
    const keywords = event.target.value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
    onChange((current) => ({
      ...current,
      ai_generation: {
        ...(current.ai_generation || {}),
        style_keywords: keywords,
      },
    }));
  };

  const handleNotesField = (field, summaryKey) => (event) => {
    const value = event.target.value;
    const summaryUpdates = summaryKey ? { [summaryKey]: value } : null;
    onChange(
      (current) => ({
        ...current,
        notes: {
          ...(current.notes || {}),
          [field]: value,
        },
      }),
      summaryUpdates
    );
  };

  return (
    <Stack spacing={2}>
      <SectionCard title="Metadata">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Program Name"
            value={metadata.garment_name || ''}
            onChange={handleMetadataField('garment_name', 'name')}
            size="small"
            fullWidth
          />
          <TextField
            label="Base Blank SKU"
            value={metadata.base_blank_sku || ''}
            onChange={handleMetadataField('base_blank_sku', 'sku')}
            size="small"
            fullWidth
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Supplier"
            value={metadata.supplier || ''}
            onChange={handleMetadataField('supplier')}
            size="small"
            fullWidth
          />
          <TextField
            label="Collection"
            value={metadata.collection || ''}
            onChange={handleMetadataField('collection', 'collection')}
            size="small"
            fullWidth
          />
          <TextField
            label="Season"
            value={metadata.season || ''}
            onChange={handleMetadataField('season', 'season')}
            size="small"
            fullWidth
          />
        </Stack>
      </SectionCard>

      <SectionCard title="Visual Assets">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Mockup (Front URL)"
            value={visualAssets.mockup_front || ''}
            onChange={handleVisualAssetField('mockup_front')}
            size="small"
            fullWidth
          />
          <TextField
            label="Mockup (Back URL)"
            value={visualAssets.mockup_back || ''}
            onChange={handleVisualAssetField('mockup_back')}
            size="small"
            fullWidth
          />
        </Stack>
        <Stack spacing={1} sx={{ mt: 2 }}>
          {colorSwatches.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Add swatches to document approved colorways.
            </Typography>
          )}
          {colorSwatches.map((swatch, index) => (
            <Stack
              key={`swatch-${index}`}
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems="flex-start"
              sx={{ border: '1px dashed', borderColor: 'divider', p: 1.5, borderRadius: 1 }}
            >
              <TextField
                label="Color Name"
                value={swatch.color_name || ''}
                onChange={handleSwatchChange(index, 'color_name')}
                size="small"
                fullWidth
              />
              <TextField
                label="Pantone"
                value={swatch.pantone_code || ''}
                onChange={handleSwatchChange(index, 'pantone_code')}
                size="small"
                fullWidth
              />
              <IconButton size="small" onClick={handleRemoveSwatch(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button startIcon={<AddIcon />} onClick={handleAddSwatch} variant="outlined" size="small">
            Add Color Swatch
          </Button>
        </Stack>
      </SectionCard>

      <SectionCard title="Embellishments">
        <Stack spacing={1}>
          {embellishments.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Document embroidery, DTF, or applique placements.
            </Typography>
          )}
          {embellishments.map((entry, index) => (
            <Stack
              key={`emb-${index}`}
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems="flex-start"
              sx={{ border: '1px dashed', borderColor: 'divider', p: 1.5, borderRadius: 1 }}
            >
              <TextField
                label="Type"
                value={entry.type || ''}
                onChange={handleEmbellishmentChange(index, 'type')}
                size="small"
                fullWidth
              />
              <TextField
                label="Placement"
                value={entry.placement || ''}
                onChange={handleEmbellishmentChange(index, 'placement')}
                size="small"
                fullWidth
              />
              <TextField
                label="Artwork URL"
                value={entry.artwork_url || ''}
                onChange={handleEmbellishmentChange(index, 'artwork_url')}
                size="small"
                fullWidth
              />
              <IconButton size="small" onClick={handleRemoveEmbellishment(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button startIcon={<AddIcon />} onClick={handleAddEmbellishment} variant="outlined" size="small">
            Add Embellishment
          </Button>
        </Stack>
      </SectionCard>

      <SectionCard title="Labeling & Packaging">
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(data.labeling_packaging?.relabel)}
              onChange={handleRelabelToggle}
              size="small"
            />
          }
          label="Replace blank labels with custom branding"
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            select
            label="Brand Label Type"
            value={labeling.brand_label_type || ''}
            onChange={handleLabelingField('brand_label_type')}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>Select type</em>
            </MenuItem>
            {BRAND_LABEL_EXECUTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Brand Label Location"
            value={labeling.brand_label_location || ''}
            onChange={handleLabelingField('brand_label_location')}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>Select location</em>
            </MenuItem>
            {BRAND_LABEL_LOCATIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        {labeling.brand_label_location === 'Other' && (
          <TextField
            label="Brand Label Notes"
            value={labeling.brand_label_notes || ''}
            onChange={handleLabelingField('brand_label_notes')}
            size="small"
            fullWidth
            sx={{ mt: 2 }}
          />
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            select
            label="Care Label Strategy"
            value={whiteLabelCareStrategy}
            onChange={handleLabelingField('care_label', 'careInstructions')}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>Select strategy</em>
            </MenuItem>
            {CARE_STRATEGIES.map((option) => (
              <MenuItem key={option} value={option}>
                {getCareStrategyLabel(option, brandName)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Care Label Notes"
            value={labeling.care_label_notes || ''}
            onChange={handleLabelingField('care_label_notes')}
            size="small"
            fullWidth
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            select
            label="Fold Method"
            value={packaging.fold_method || ''}
            onChange={handlePackagingField('fold_method')}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>Select method</em>
            </MenuItem>
            {PACKAGING_FOLD_METHODS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Bag Type"
            value={packaging.bag_type || ''}
            onChange={handlePackagingField('bag_type')}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>Select bag</em>
            </MenuItem>
            {PACKAGING_BAG_TYPES.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
          {PACKAGING_EXTRAS.map((extra) => (
            <FormControlLabel
              key={extra}
              control={
                <Checkbox
                  size="small"
                  checked={Array.isArray(packaging.extras) && packaging.extras.includes(extra)}
                  onChange={handlePackagingExtrasToggle(extra)}
                />
              }
              label={extra}
            />
          ))}
        </Stack>
        <TextField
          label="Packaging Notes"
          value={packaging.notes || ''}
          onChange={handlePackagingField('notes')}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
      </SectionCard>

      <SectionCard title="AI Guidance & Notes">
        <TextField
          label="Style Keywords"
          value={styleKeywordValue}
          onChange={handleStyleKeywords}
          helperText="Comma separated list, e.g., premium cotton, oversized, minimal"
          size="small"
          fullWidth
        />
        <TextField
          label="Background Style"
          value={data.ai_generation?.background_style || ''}
          onChange={(event) =>
            onChange((current) => ({
              ...current,
              ai_generation: {
                ...(current.ai_generation || {}),
                background_style: event.target.value,
              },
            }))
          }
          size="small"
          fullWidth
          sx={{ mt: 2 }}
        />
        <TextField
          label="Creative Notes"
          value={data.notes?.creative_notes || ''}
          onChange={handleNotesField('creative_notes', 'technicalNotes')}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
        <TextField
          label="Fulfillment Notes"
          value={data.notes?.fulfillment_notes || ''}
          onChange={handleNotesField('fulfillment_notes', 'productionNotes')}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
      </SectionCard>
    </Stack>
  );
};

export default DesignStudioPage;
